/*
 * ====================================================================================
 * FILE: MEWPDetailPage.jsx
 * PURPOSE: Displays detailed information about a specific Mobile Elevating Work Platform (MEWP).
 * 
 * DESCRIPTION:
 * This page acts as the "Command Center" for a single machine. It shows:
 * 1. Identity & Status: Model, Brand, and current working status (Available, On Hire, etc.).
 * 2. Real-Time Metrics: Engine hours, Fuel level, and active Alarms.
 * 3. Location: A map showing where the machine is.
 * 4. Logs & History: A searchable list of everything the machine has done.
 * 5. Live Data: Technical sensor readings (Battery, Tilt, Pressure, etc.).
 * 
 * DATA SOURCE:
 * - Full Device Details: Fetched via mewpService.getDetails using the ID from the URL.
 *   - Note: The URL ID (canonicalId) corresponds to the 'equipment_id' (e.g. MEWP-001).
 * - Logs & History: Fetched via mewpService.getLogDates / getDeviceHistory using the technical 'device_id'.
 * 
 * AUTHOR: Development Team
 * DATE: 2024
 * ====================================================================================
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom'; // Hooks for navigation and URL parameters
import { MapContainer, TileLayer, Marker, Circle, Popup, Polyline } from 'react-leaflet'; // Map components
import L from 'leaflet'; // Core Leaflet library

// --- UI Components (Material UI) ---
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Avatar,
  Chip,
  Button,
  Stack,
  IconButton,
  Divider,
  TextField,
  InputAdornment,
  Breadcrumbs,
  Link as MuiLink,
  Tooltip,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  FormControl,
  Select,
  MenuItem,
  Autocomplete,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputLabel,
  CircularProgress,
  Pagination
} from '@mui/material';

// --- Icons ---
import {
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  AccessTime as ClockIcon,
  Search as SearchIcon,
  Map as MapIcon,
  Layers as LayersIcon,
  ChevronRight as ChevronRightIcon,
  Launch as ExternalLinkIcon,
  Info as InfoIcon,
  Settings as SettingsIcon,
  Warning as AlertCircleIcon,
  GetApp as DownloadIcon,
  LocalGasStation as FuelIcon,
  History as HistoryIcon,
  Description as FileIcon,
  CheckCircle as CheckCircleIcon,
  Edit as EditIcon,
  Assignment as AssignmentIcon,
  Speed as LiveIcon,
  CalendarToday,
  TableView,
  PictureAsPdf,
  ArrowBack,
  PowerSettingsNew as PowerIcon,
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  VpnKey as KeyIcon,
  SatelliteAlt as GpsIcon,
  Insights as AnalyticsIcon,
  Timeline as TimelineIcon,
  Check as CheckIcon,
  Tune as TuneIcon,
  NoteAdd as NoteAddIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  UnfoldMore as StfIcon,
  PersonOutline as OperatorIcon,
  BadgeOutlined as EmpIdIcon,
  PhoneIphone as PhoneIcon,
} from '@mui/icons-material';

// --- Services & APIs ---
// These files handle communication with our backend server.
import apiClient from '../../services/apiClient';
import mewpService from '../../services/mewpService';
// The unified alarms feed (computed alarms + device-reported CAN alerts/DTCs) —
// used by the Visualization view's errors panel.
import geofenceService from '../../services/geofenceService';
import equipmentSpecsService from '../../services/equipmentSpecsService';
// Machine operators (machine_operators + the operators master) — who is on this
// machine, their emp id and a number to ring.
import rentalService from '../../services/rentalService';

// --- Machine lifecycle status (asset_tracking) ---
import { useAuth } from '../../context/AuthContext';
import { canChangeMachineStatus } from '../../utils/permissions';
import { statusLabel, statusColor } from '../../utils/statusLabels';
import { useDeviceStore } from '../../store/DeviceStore';
import { resolveDeviceStatus } from '../../utils/liveStatus';
import LiveStatusBadge from '../ui/LiveStatusBadge';
import ChangeStatusDialog from './ChangeStatusDialog';

// --- Charting Library ---
// Used for drawing graphs and charts (e.g., Utilization).
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  Filler
} from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';

// --- Map Styles & Assets ---
import 'leaflet/dist/leaflet.css'; // Leaflet CSS
import icon from 'leaflet/dist/images/marker-icon.png'; // Default Marker Icon
import iconShadow from 'leaflet/dist/images/marker-shadow.png'; // Marker Shadow

// --- Custom Hooks & Utils ---
import useTelemetry from '../../hooks/useTelemetry'; // Hook to handle live data
import { formatToIST } from '../../utils/dataUtils'; // Helper to format dates
import { formatParameterValue, formatParameterName, shortSensorName } from '../../utils/deviceUtils'; // Helpers to format sensor values / labels
import ServiceMachineDetailPage from './ServiceMachineDetailPage'; // The "Details" view: this machine's service record
import { machineLabel } from '../../utils/machineLabel';
import MachinePostureCard from './MachinePostureCard';

// --- Configuration ---

// 1. Register ChartJS components so they can be rendered.
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  ChartTooltip,
  Legend,
  Filler
);

// 2. Fix for Leaflet Marker Icons usually missing in React bundles.
let DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});
L.Marker.prototype.options.icon = DefaultIcon;

// 3. Day helpers for the errors panel. Alarm `created_at` is stored in UTC while
//    the whole UI (and the daily report that links here) works in IST days, so an
//    alarm is placed on the IST calendar day it actually happened on.
const shiftDay = (ymd, n) => {
  const d = new Date(`${ymd}T00:00:00Z`);
  if (isNaN(d.getTime())) return ymd;
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};
const istDayOf = (utcTs) => {
  const d = new Date(utcTs);
  return isNaN(d.getTime()) ? null : d.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
};

// Severity → the accent used for its chip and its card's left edge. `error` and
// `critical` are the same thing spelled two ways across the alarm sources.
const SEVERITY_TONE = {
  critical: { color: '#dc2626', label: 'ERROR' },
  error:    { color: '#dc2626', label: 'ERROR' },
  warning:  { color: '#d97706', label: 'WARNING' },
  info:     { color: '#2563eb', label: 'INFO' },
};
const toneOf = (sev) => SEVERITY_TONE[String(sev || '').toLowerCase()] || SEVERITY_TONE.info;
// Device-reported (the machine's own CAN alert/DTC) vs server-computed threshold
// or geofence alarms — the daily report draws the same distinction.
// Threshold verdicts from GET /api/equipment-specs/machine/:id/thresholds.
// Colour never travels alone — every tone carries the word for what it means.
const THRESHOLD_TONE = {
  breach:     { token: 'error.main',     label: 'Out of range' },
  warning:    { token: 'warning.main',   label: 'Near limit' },
  ok:         { token: 'success.main',   label: 'In range' },
  idle:       { token: 'text.secondary', label: 'Engine off' },   // check only runs while running
  invalid:    { token: 'text.disabled',  label: 'Bad reading' },  // sensor "no-data" / corrupt frame
  'no-data':  { token: 'text.disabled',  label: 'Not reported' },
};

// The published band as one line: hard bounds, with the softer warning band
// after it when the spec defines one ("≤ 107 °C · warn ≤ 100").
const formatThresholdBand = (t) => {
  const u = t.unit ? ` ${t.unit}` : '';
  const num = (n) => (Number.isInteger(n) ? n : Number(n).toFixed(1));
  let hard;
  if (t.min != null && t.max != null) hard = `${num(t.min)} to ${num(t.max)}${u}`;
  else if (t.max != null) hard = `\u2264 ${num(t.max)}${u}`;
  else if (t.min != null) hard = `\u2265 ${num(t.min)}${u}`;
  else hard = 'no hard limit';

  const warn = [];
  if (t.warnMin != null) warn.push(`\u2265 ${num(t.warnMin)}`);
  if (t.warnMax != null) warn.push(`\u2264 ${num(t.warnMax)}`);
  return warn.length ? `${hard} \u00b7 warn ${warn.join(' / ')}` : hard;
};

const isDeviceReportedAlarm = (a) => a.category === 'DEVICE_ALERT' || a.category === 'DEVICE_DTC';

/**
 * Main Component: MEWPDetailPage
 * 
 * This is the main function that renders the page.
 */
const MEWPDetailPage = () => {
  // --- 1. State & Hooks Configuration ---

  // Get the ID from the URL. 
  // NOTE: This is renamed to 'urlId' to clarify it might be an equipment_id (e.g. MEWP-001) or a device_id.
  const { deviceId: urlId } = useParams();

  // Hook to change pages programmatically
  const navigate = useNavigate();

  /* Deep-link parameters — used by the Service Daily Report so that clicking a
     machine row lands on WHAT HAPPENED TO THAT MACHINE ON THAT DAY, not on a
     generic page the user then has to drive by hand:
       ?view=viz|logs|dashboard|service   which section opens
       ?date=YYYY-MM-DD                   the day to load (viz range = that one day)
       ?at=<ISO timestamp>                select the frame the machine sent at that
                                          instant — this is how "click a CAN error
                                          → show what really happened" works: the
                                          map, the activity log and every signal in
                                          the Data Log switch to that moment
       ?alarm=<name>                      the error you arrived on, named in a banner
       ?from=daily-report                 show a "Daily Report" breadcrumb back
     Read once into the initial state so the page opens already on target. */
  const [searchParams] = useSearchParams();
  const DEEP_VIEWS = ['dashboard', 'logs', 'viz', 'service'];
  const deepView = DEEP_VIEWS.includes(searchParams.get('view')) ? searchParams.get('view') : null;
  const deepDate = /^\d{4}-\d{2}-\d{2}$/.test(searchParams.get('date') || '') ? searchParams.get('date') : null;
  const deepAt = searchParams.get('at');
  const deepAlarm = searchParams.get('alarm');
  const cameFromDailyReport = searchParams.get('from') === 'daily-report';
  // Opened from an equipment list (Service/Operations/Workspace) — the
  // Equipment breadcrumb steps BACK to that list, which restores its filters,
  // instead of jumping to the Workspace /equipment page.
  const cameFromEquipmentList = !!useLocation().state?.fromEquipmentList;
  const goEquipment = () => (cameFromEquipmentList ? navigate(-1) : navigate('/equipment'));

  // State to store the full details of the device fetched from API
  const [deviceDetails, setDeviceDetails] = useState(null);

  // State to track if we are currently loading data (shows spinner if true)
  const [loadingDetails, setLoadingDetails] = useState(true);

  // State for the search bar in the sensors table
  const [searchTerm, setSearchTerm] = useState('');

  // State for toggling Map type (Satellite vs Standard Map)
  const [mapType, setMapType] = useState('satellite');

  // State to control which main view is active: 'dashboard' (Main view) or 'logs' (History view)
  const [view, setView] = useState(deepView || 'dashboard');

  // Custom hook: gets live telemetry status if available
  // We use urlId here, but internally the hook/server might need to resolve it if it's an equipment_id.
  const { device: storeDevice, status } = useTelemetry(urlId);

  // Pagination for logs list
  const [page, setPage] = useState(1);
  const rowsPerPage = 30;

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  // --- 2. Data Fetching Functions ---

  /**
   * fetchDetails
   * Purpose: Asynchronously contacts the server to reach full details for this device.
   * Logic: We pass the urlId (which is expected to be the Equipment ID, e.g. MEWP-001).
   *        The backend service 'getDetails' handles looking up by Equipment ID or Device ID.
   * Trigger: Runs when the component mounts or urlId changes.
   */
  const fetchDetails = useCallback(async () => {
    try {
      setLoadingDetails(true); // Start loading

      // Request details using the ID from URL (Equipment ID)
      const res = await mewpService.getDetails(urlId);

      setDeviceDetails(res.data || res); // Save result
    } catch (err) {
      console.error('Failed to fetch MEWP details:', err);
    } finally {
      setLoadingDetails(false); // Stop loading regardless of success/failure
    }
  }, [urlId]);

  // Effect: Run fetchDetails when urlId exists & poll every 15s for "Live Data" updates
  useEffect(() => {
    if (urlId) {
      fetchDetails();
      const pollInterval = setInterval(fetchDetails, 15000); // Poll every 15s
      return () => clearInterval(pollInterval);
    }
  }, [fetchDetails, urlId]);

  // --- Real-time device status from backend (KEY_ON / ACTIVE / KEY_OFF) ---
  const [deviceLiveStatus, setDeviceLiveStatus] = useState({ status: null, lastDataAt: null, recentActivity: [], loading: true });

  // Poll backend /live-status every 30 seconds
  useEffect(() => {
    if (!urlId) return;
    const fetchLiveStatus = async () => {
      try {
        const res = await mewpService.getDeviceLiveStatus(urlId);
        const data = res.data || res;
        setDeviceLiveStatus({
          status: data.status, // KEY_ON | ACTIVE | KEY_OFF
          lastDataAt: data.lastDataAt,
          recentActivity: data.recentActivity || [],
          loading: false
        });
      } catch (err) {
        console.error('Live status poll error:', err);
        setDeviceLiveStatus(prev => ({ ...prev, loading: false }));
      }
    };
    fetchLiveStatus(); // Fetch immediately
    const pollInterval = setInterval(fetchLiveStatus, 30000); // Then poll every 30s (fallback to the live socket below)
    return () => clearInterval(pollInterval);
  }, [urlId]);

  // Real-time override: the global DeviceStore receives MQTT frames over Socket.IO,
  // so prefer its live frame for this machine (instant) and fall back to the poll.
  const { state: liveDevicesState } = useDeviceStore();
  const liveFrame = useMemo(
    () => (liveDevicesState?.devices || []).find(
      d => String(d.device_id) === String(urlId) || String(d.equipment_id) === String(urlId)
    ),
    [liveDevicesState?.devices, urlId]
  );
  const liveStatusCode = resolveDeviceStatus(
    liveFrame || { live_status: deviceLiveStatus.status, last_seen: deviceLiveStatus.lastDataAt }
  );
  const liveLastSeen = liveFrame?.__liveTs || liveFrame?.last_seen || deviceLiveStatus.lastDataAt;

  // The LIVE pose for the machine-position rig: raw sensor values from the
  // 15s poll (keys are the same SPN/named parameter ids the frames carry),
  // with the socket frame layered on top when one has arrived (freshest wins).
  const livePostureFrame = useMemo(() => {
    const f = {};
    (deviceDetails?.sensors || []).forEach(s => { if (s && s.id != null) f[s.id] = s.value; });
    Object.assign(f, liveFrame || {});
    f.timestamp = liveLastSeen || f.timestamp || null;
    return Object.keys(f).length > 1 || f.timestamp ? f : null;
  }, [deviceDetails, liveFrame, liveLastSeen]);

  // --- Machine lifecycle status (asset_tracking) ---
  const { user } = useAuth();
  const canChangeStatus = canChangeMachineStatus(user);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [statusHistoryOpen, setStatusHistoryOpen] = useState(false);
  const [statusHistory, setStatusHistory] = useState([]);

  const fetchStatusHistory = useCallback(async () => {
    try {
      const res = await mewpService.getStatusHistory(urlId);
      setStatusHistory(res.data || []);
    } catch (err) {
      console.error('Status history fetch error:', err);
    }
  }, [urlId]);

  // Load history when the timeline dialog opens
  useEffect(() => {
    if (statusHistoryOpen) fetchStatusHistory();
  }, [statusHistoryOpen, fetchStatusHistory]);

  // --- Operating thresholds for THIS machine ---
  // The safe band of every telemetry field for the machine's model, with its
  // latest reading already judged against it server-side by the same code the
  // alarm engine runs. Re-read whenever a fresh frame lands, so the card tracks
  // the live value rather than the value at page load.
  const [thresholds, setThresholds] = useState([]);
  const [thresholdMeta, setThresholdMeta] = useState(null);
  const [thresholdsLoading, setThresholdsLoading] = useState(true);
  const [thresholdsError, setThresholdsError] = useState(false);
  useEffect(() => {
    if (!urlId) { setThresholdsLoading(false); return undefined; }
    let cancelled = false;
    (async () => {
      try {
        setThresholdsError(false);
        const res = await equipmentSpecsService.getMachineThresholds(urlId);
        if (cancelled) return;
        setThresholds(Array.isArray(res?.data) ? res.data : []);
        setThresholdMeta(res?.meta || null);
      } catch (err) {
        console.error('Machine thresholds fetch error:', err);
        if (!cancelled) { setThresholds([]); setThresholdMeta(null); setThresholdsError(true); }
      } finally {
        if (!cancelled) setThresholdsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [urlId, liveLastSeen]);

  // Out-of-band first (breach, then warning), then the rest in spec order —
  // the reason to open this card is at the top of it.
  const thresholdRows = useMemo(() => {
    const rank = { breach: 0, warning: 1, ok: 2, idle: 3, invalid: 4, 'no-data': 5 };
    return [...thresholds].sort((a, b) => (rank[a.status] ?? 9) - (rank[b.status] ?? 9));
  }, [thresholds]);

  const thresholdsOutOfBand = useMemo(
    () => thresholds.filter(t => t.status === 'breach' || t.status === 'warning').length,
    [thresholds]
  );

  // --- 3. Additional Logic ---
  const [refreshingLive, setRefreshingLive] = useState(false);

  /**
   * handleCheckUpdate
   * Purpose: Manually triggers an MQTT update command to the device to refresh data.
   */
  const handleCheckUpdate = async () => {
    try {
      setRefreshingLive(true);
      const res = await mewpService.checkAndUpdateDevice(urlId);
      const data = res.data || res;
      if (data.success) {
        // Wait 2.5 seconds for device to respond and refresh data
        setTimeout(async () => {
          await fetchDetails();
          setRefreshingLive(false);
        }, 2500);
      } else {
        setRefreshingLive(false);
        alert(data.message || 'Failed to trigger device update');
      }
    } catch (err) {
      console.error('Check update error:', err);
      setRefreshingLive(false);
      alert('Communication error with broker.');
    }
  };

  // --- Helper: Resolve Correct ID for Logs ---
  // The Logs API requires the technical 'device_id' (telemetry ID).
  // The URL might contain the 'equipment_id' (Business ID).
  // We extract the correct ID from the fetched details.
  const technicalDeviceId = useMemo(() => {
    return deviceDetails?.device?.device_id || urlId;
  }, [deviceDetails, urlId]);

  // --- 3. Logs & History Logic ---

  /* 
   * The Logs section allows users to pick a date and see every event that happened.
   * This logic handles fetching dates, fetching specific logs, and summarizing them.
   */

  const [logDates, setLogDates] = useState([]); // List of dates available
  const [logs, setLogs] = useState([]); // The detailed logs for a selected date
  const [selectedDate, setSelectedDate] = useState(null); // The currently selected date
  const [loadingLogs, setLoadingLogs] = useState(false); // Loading state for logs
  const [dateSearchTerm, setDateSearchTerm] = useState(''); // Filter for the date list
  const [logSearchTerm, setLogSearchTerm] = useState(''); // Filter for the specific log entries
  const [logPage, setLogPage] = useState(1); // Current page for the log detail view
  const LOGS_PER_PAGE = 100; // Show 100 log entries per page
  const [dailySummary, setDailySummary] = useState(null); // Summary stats for the selected day
  const [selectedDateManualEntry, setSelectedDateManualEntry] = useState(null); // Manual entry for the selected day

  // PDF/Excel date range dialog states
  const [downloadRangeOpen, setDownloadRangeOpen] = useState(false);
  const [downloadType, setDownloadType] = useState('pdf');
  const [downloadFromDate, setDownloadFromDate] = useState('');
  const [downloadToDate, setDownloadToDate] = useState('');

  // --- 4. Visualization & Analytics Logic (NEW) ---
  // Seeded from ?date= when deep-linked, so the "default to the latest log date"
  // effect below (which only fires while both bounds are empty) stands down and
  // the requested day is what loads.
  const [vizFromDate, setVizFromDate] = useState(deepDate || '');
  const [vizToDate, setVizToDate] = useState(deepDate || '');
  const [vizData, setVizData] = useState([]); // Flattened history records for all selected dates
  const [vizActivities, setVizActivities] = useState([]); // Processed activity logs from backend
  const [loadingViz, setLoadingViz] = useState(false);
  const [selectedVizTime, setSelectedVizTime] = useState(null); // Selected specific time data point
  const [leftTrendParams, setLeftTrendParams] = useState([]);
  const [rightTrendParams, setRightTrendParams] = useState([]);
  const [vizApplied, setVizApplied] = useState(false); // Whether user has clicked Apply to load data

  // Raw device-reported rows for the selected day range — the "Device Events"
  // panel beside the Data Log: device_alerts (power, CAN link, battery…) and
  // device_dtc (J1939 SPN/FMI fault codes). Fetched whenever the viz dates
  // change (cheap, bounded query), so the deep-linked day shows immediately.
  const [vizEvents, setVizEvents] = useState({ alerts: [], dtcs: [] });
  useEffect(() => {
    if (view !== 'viz' || !technicalDeviceId || !vizFromDate || !vizToDate) return;
    let alive = true;
    mewpService.getDeviceEvents(technicalDeviceId, vizFromDate, vizToDate)
      .then(r => { const d = r.data || r; if (alive) setVizEvents({ alerts: d.alerts || [], dtcs: d.dtcs || [] }); })
      .catch(() => { if (alive) setVizEvents({ alerts: [], dtcs: [] }); });
    return () => { alive = false; };
  }, [view, technicalDeviceId, vizFromDate, vizToDate]);

  // STF (Scroll To change Frame): when ON, mouse-wheel or ↑/↓ steps through the
  // log frames in the LOG SELECTION box, one frame at a time.
  const [stfEnabled, setStfEnabled] = useState(false);
  const stfRef = useRef(null);
  const frameIdxRef = useRef(-1);   // current frame index (source of truth for stepping)
  const wheelLockRef = useRef(0);   // throttle: one step per scroll notch

  // Keep the index in sync when the frame is chosen from the dropdown / cleared.
  useEffect(() => {
    if (!selectedVizTime) { frameIdxRef.current = -1; return; }
    const i = vizData.findIndex(f =>
      f === selectedVizTime ||
      (selectedVizTime.id != null && f.id === selectedVizTime.id) ||
      (selectedVizTime.timestamp && f.timestamp === selectedVizTime.timestamp));
    if (i !== -1) frameIdxRef.current = i;
  }, [selectedVizTime, vizData]);

  // Step exactly ONE frame from the current index (vizData is oldest→newest, so
  // +1 = next/later time, -1 = previous/earlier time).
  const stepFrame = useCallback((dir) => {
    if (!vizData || vizData.length === 0) return;
    let i = frameIdxRef.current;
    if (i < 0) i = dir > 0 ? -1 : 0; // from "All Logs": first step lands on frame 0
    const n = Math.max(0, Math.min(vizData.length - 1, i + dir));
    frameIdxRef.current = n;
    setSelectedVizTime(vizData[n]);
  }, [vizData]);

  // Wheel needs a non-passive listener to preventDefault page scroll. A single
  // scroll fires several wheel events — throttle so one notch = one frame.
  useEffect(() => {
    const el = stfRef.current;
    if (!el || !stfEnabled) return undefined;
    const onWheel = (e) => {
      e.preventDefault();
      const now = Date.now();
      if (now - wheelLockRef.current < 120) return; // ignore the burst from one notch
      wheelLockRef.current = now;
      stepFrame(e.deltaY > 0 ? 1 : -1);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [stfEnabled, stepFrame]);

  // --- Manual Log Entry State ---
  const [manualLogOpen, setManualLogOpen] = useState(false);
  const [manualLogs, setManualLogs] = useState([]);
  const [manualLogForm, setManualLogForm] = useState({
    log_date: '', first_key_on: '', last_key_off: '',
    engine_hours: '', fuel_used: '', alert_type: '',
    alert_message: '', notes: '', entered_by: ''
  });
  const [manualLogEditId, setManualLogEditId] = useState(null);
  const [manualLogSaving, setManualLogSaving] = useState(false);

  // Fetch manual logs
  const fetchManualLogs = useCallback(async () => {
    if (!urlId) return;
    try {
      const res = await apiClient.get(`/manual-logs/${urlId}`);
      setManualLogs(res.data?.data || []);
    } catch (err) { console.error('Manual logs fetch error:', err); }
  }, [urlId]);

  useEffect(() => { if (urlId) fetchManualLogs(); }, [fetchManualLogs, urlId]);

  // Save manual log
  const handleSaveManualLog = async () => {
    if (!manualLogForm.log_date) return;
    setManualLogSaving(true);
    try {
      const payload = { ...manualLogForm, equipment_id: urlId };
      if (manualLogEditId) {
        await apiClient.put(`/manual-logs/${manualLogEditId}`, payload);
      } else {
        await apiClient.post('/manual-logs', payload);
      }
      setManualLogForm({ log_date: '', first_key_on: '', last_key_off: '', engine_hours: '', fuel_used: '', alert_type: '', alert_message: '', notes: '', entered_by: '' });
      setManualLogEditId(null);
      fetchManualLogs();
    } catch (err) { console.error('Save manual log error:', err); }
    setManualLogSaving(false);
  };

  // Delete manual log
  const handleDeleteManualLog = async (id) => {
    try {
      await apiClient.delete(`/manual-logs/${id}`);
      fetchManualLogs();
    } catch (err) { console.error('Delete manual log error:', err); }
  };

  // Edit: load a manual log into form
  const handleEditManualLog = (log) => {
    setManualLogForm({
      log_date: log.log_date ? new Date(log.log_date).toISOString().split('T')[0] : '',
      first_key_on: log.first_key_on || '',
      last_key_off: log.last_key_off || '',
      engine_hours: log.engine_hours || '',
      fuel_used: log.fuel_used || '',
      alert_type: log.alert_type || '',
      alert_message: log.alert_message || '',
      notes: log.notes || '',
      entered_by: log.entered_by || ''
    });
    setManualLogEditId(log.id);
    setManualLogOpen(true);
  };

  // Helper to fetch data for visualization
  const fetchVizData = useCallback(async (dates) => {
    if (dates.length === 0) {
      setVizData([]);
      return;
    }
    if (!technicalDeviceId) {
      return;
    }
    setLoadingViz(true);
    try {
      // Fetch multiple dates in parallel
      const requests = dates.map(date => mewpService.getDeviceHistory(technicalDeviceId, 1000, date));
      const results = await Promise.all(requests);

      // Combine all history arrays
      let combined = results.reduce((acc, current) => {
        return acc.concat(current.history || []);
      }, []);

      // Sort by timestamp ASC (earliest first) for graph calculation
      combined.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

      setVizData(combined);

      // Combine all activities from parallel requests
      let combinedActivities = results.reduce((acc, current) => {
        return acc.concat(current.activities || []);
      }, []);

      // Sort by timestamp DESC (newest first)
      combinedActivities.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      setVizActivities(combinedActivities);

      if (combined.length > 0) {
        const allKeys = new Set();
        combined.forEach(item => Object.keys(item).forEach(k => allKeys.add(k)));
        const possible = Array.from(allKeys).filter(k => !['id', 'device_id', 'timestamp', 'latitude', 'longitude', 'lat', 'lon', 'location', 'fields', 'time', 'fullTime'].includes(k)).sort();

        setLeftTrendParams(prev => {
          if (prev.length > 0) return prev;
          return possible.length > 0 ? [possible[0]] : [];
        });
        setRightTrendParams(prev => {
          if (prev.length > 0) return prev;
          return possible.length > 1 ? [possible[1]] : (possible.length > 0 ? [possible[0]] : []);
        });
      }

    } catch (err) {
      console.error("Viz fetch failed:", err);
    } finally {
      setLoadingViz(false);
    }
  }, [technicalDeviceId]);

  // Default bounds to latest logdate
  useEffect(() => {
    if (view === 'viz' && logDates.length > 0 && !vizFromDate && !vizToDate) {
      const latestDate = logDates.reduce((max, d) => d.date > max ? d.date : max, logDates[0].date);
      setVizFromDate(latestDate);
      setVizToDate(latestDate);
    }
  }, [view, logDates, vizFromDate, vizToDate]);

  // Days to load for a from→to range. Normally this is the intersection with the
  // dates that HAVE data (logDates), which keeps a wide range cheap. When that
  // intersection is empty the range is enumerated instead (capped, so a stray
  // decade-wide range can't fan out): asking for a day the machine was silent on
  // must still load — and come back empty — rather than silently do nothing.
  const MAX_ENUMERATED_DAYS = 31;
  const vizDatesFor = useCallback((from, to) => {
    const inRange = logDates.filter(d => d.date >= from && d.date <= to).map(d => d.date);
    if (inRange.length > 0) return inRange;
    const out = [];
    for (let d = new Date(`${from}T00:00:00Z`); d <= new Date(`${to}T00:00:00Z`); d.setUTCDate(d.getUTCDate() + 1)) {
      out.push(d.toISOString().slice(0, 10));
      if (out.length >= MAX_ENUMERATED_DAYS) break;
    }
    return out;
  }, [logDates]);

  // Manual Apply handler: only fetch viz data when user clicks Apply button
  const handleApplyViz = useCallback(() => {
    if (vizFromDate && vizToDate) {
      setVizApplied(true);
      fetchVizData(vizDatesFor(vizFromDate, vizToDate));
    }
  }, [vizFromDate, vizToDate, fetchVizData, vizDatesFor]);

  // Reset vizApplied when dates change so user knows they need to re-apply
  useEffect(() => {
    setVizApplied(false);
  }, [vizFromDate, vizToDate]);

  /* Deep link (?view=viz&date=…): load that day straight away instead of leaving
     the user staring at a pulsing Apply button. Runs once, and only after the
     device has resolved so the history request carries the technical id. */
  const deepVizAppliedRef = useRef(false);
  useEffect(() => {
    if (deepVizAppliedRef.current) return;
    if (!deepDate || deepView !== 'viz') return;
    if (loadingDetails || !technicalDeviceId) return;
    deepVizAppliedRef.current = true;
    setVizApplied(true);
    fetchVizData([deepDate]);
  }, [deepDate, deepView, loadingDetails, technicalDeviceId, fetchVizData]);

  /* ── Errors & alerts on the visualisation ────────────────────────────────
     What the machine (or the health checkers) reported during the period on
     screen. Selecting one moves the whole view — map, activity log, Data Log —
     to the frame closest to it, which is what turns "an alert happened" into
     "here is what the machine was doing when it happened". */
  const [vizAlarms, setVizAlarms] = useState([]);
  const [loadingVizAlarms, setLoadingVizAlarms] = useState(false);
  const [selectedAlarmId, setSelectedAlarmId] = useState(null);

  // Pick the frame nearest an instant. Nearest, not exact: an alert and a
  // telemetry frame arrive on two different MQTT topics, seconds apart.
  const selectFrameNearest = useCallback((iso) => {
    const t = new Date(iso).getTime();
    if (isNaN(t) || !vizData || vizData.length === 0) return false;
    let best = null, bestGap = Infinity;
    for (const f of vizData) {
      const gap = Math.abs(new Date(f.timestamp).getTime() - t);
      if (!isNaN(gap) && gap < bestGap) { bestGap = gap; best = f; }
    }
    if (!best) return false;
    setSelectedVizTime(best);
    return true;
  }, [vizData]);

  const handleAlarmClick = useCallback((a) => {
    setSelectedAlarmId(a.id);
    if (!selectFrameNearest(a.created_at)) setSelectedVizTime(null);
  }, [selectFrameNearest]);

  /* Deep link (?at=…): once the day's frames land, jump to the alarm's frame.
     Guarded by a ref so a later manual selection is never yanked back. */
  const deepAtDoneRef = useRef(false);
  useEffect(() => {
    if (deepAtDoneRef.current || !deepAt || !vizData || vizData.length === 0) return;
    if (selectFrameNearest(deepAt)) deepAtDoneRef.current = true;
  }, [deepAt, vizData, selectFrameNearest]);

  // --- Machine operators (who is on this machine) ------------------------
  // The assignment itself lives in machine_operators (name, shift, from/to
  // dates, added while On-Rent). The server resolves each assignment against
  // the operators master — the 1,182-strong field roster, no login accounts —
  // so every row also carries the emp id and a mobile number to ring.
  // Fetched once per machine: an assignment is entered by hand, it does not
  // arrive with telemetry, so it is deliberately outside the 15s poll.
  const [operators, setOperators] = useState([]);
  const [loadingOperators, setLoadingOperators] = useState(true);
  const equipmentIdForOperators = deviceDetails?.device?.equipment_id || urlId;
  useEffect(() => {
    if (!equipmentIdForOperators) return;
    let cancelled = false;
    (async () => {
      setLoadingOperators(true);
      try {
        const res = await rentalService.getOperators(equipmentIdForOperators);
        if (!cancelled) setOperators(res?.operators || []);
      } catch (err) {
        console.error('Failed to fetch machine operators:', err);
        if (!cancelled) setOperators([]);
      } finally {
        if (!cancelled) setLoadingOperators(false);
      }
    })();
    return () => { cancelled = true; };
  }, [equipmentIdForOperators]);

  // Assignments still open today are the crew on the machine now; the rest are
  // past hands, counted but not listed (the rental page owns that full table).
  const activeOperators = useMemo(() => operators.filter((o) => Number(o.active) === 1), [operators]);
  const pastOperatorCount = operators.length - activeOperators.length;

  // The alarms raised in the applied range. Queried a day either side because
  // the endpoint filters on UTC `created_at`, then narrowed to the IST days on
  // screen so the list matches the day the daily report reported.
  const equipmentIdForAlarms = deviceDetails?.device?.equipment_id || urlId;
  useEffect(() => {
    if (view !== 'viz' || !vizApplied || !vizFromDate || !vizToDate || !equipmentIdForAlarms) return;
    let cancelled = false;
    (async () => {
      setLoadingVizAlarms(true);
      try {
        const res = await geofenceService.listAlarms({
          equipment_id: equipmentIdForAlarms,
          from: shiftDay(vizFromDate, -1),
          to: shiftDay(vizToDate, 1),
          limit: 200,
        });
        const rows = Array.isArray(res?.data) ? res.data : [];
        const inRange = rows.filter((a) => {
          const day = istDayOf(a.created_at);
          return day && day >= vizFromDate && day <= vizToDate;
        });
        // Oldest first, so the strip reads left-to-right like the day did.
        inRange.sort((x, y) => new Date(x.created_at) - new Date(y.created_at));
        if (!cancelled) setVizAlarms(inRange);
      } catch (err) {
        console.error('Failed to fetch alarms for visualization:', err);
        if (!cancelled) setVizAlarms([]);
      } finally {
        if (!cancelled) setLoadingVizAlarms(false);
      }
    })();
    return () => { cancelled = true; };
  }, [view, vizApplied, vizFromDate, vizToDate, equipmentIdForAlarms]);

  // Mark the deep-linked alarm as the selected one, so the card you arrived on
  // is the highlighted card in the strip.
  useEffect(() => {
    if (!deepAt || selectedAlarmId || vizAlarms.length === 0) return;
    const t = new Date(deepAt).getTime();
    if (isNaN(t)) return;
    let best = null, bestGap = Infinity;
    for (const a of vizAlarms) {
      const gap = Math.abs(new Date(a.created_at).getTime() - t);
      if (!isNaN(gap) && gap < bestGap) { bestGap = gap; best = a; }
    }
    // Only when it really is the same event (within a minute), not just the
    // nearest one in a day that happens to hold something else.
    if (best && bestGap <= 60000) setSelectedAlarmId(best.id);
  }, [deepAt, vizAlarms, selectedAlarmId]);

  /* Deep link (?view=logs&date=…): open that day's frame list directly. */
  const deepLogsRef = useRef(false);
  useEffect(() => {
    if (deepLogsRef.current) return;
    if (!deepDate || deepView !== 'logs') return;
    if (loadingDetails || !technicalDeviceId) return;
    deepLogsRef.current = true;
    handleDateSelect(deepDate);
    // handleDateSelect is stable for this purpose (it only reads technicalDeviceId,
    // which this effect already waits for) — re-running on its identity would
    // refetch the day on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepDate, deepView, loadingDetails, technicalDeviceId]);

  // --- 5. Logs & History Logic ---
  useEffect(() => {
    if ((view === 'logs' || view === 'viz') && technicalDeviceId) {
      const fetchDates = async () => {
        try {
          setLoadingLogs(true);
          // Get list of dates with data using the resolved technical ID
          const res = await mewpService.getLogDates(technicalDeviceId);
          if (res && res.dates) {
            setLogDates(res.dates);
          }
        } catch (err) {
          console.error("Failed to fetch log dates:", err);
        } finally {
          setLoadingLogs(false);
        }
      };
      fetchDates();
    }
  }, [view, technicalDeviceId]);

  // Effect: Auto-select latest date if viz view is active and none selected
  // (Replaced by bounds logic above)


  /**
   * fetchLogsForDate
   * Purpose: Gets the detailed history for a specific day.
   * @param {string} date - The date to fetch (YYYY-MM-DD)
   */
  const fetchLogsForDate = async (date) => {
    try {
      setLoadingLogs(true);
      // Fetch history (limit 1000 records for safety)
      const res = await mewpService.getDeviceHistory(technicalDeviceId, 1000, date);

      if (res) {
        setSelectedDateManualEntry(res.manual_entry || null);
        
        // Map history entries
        const formatted = (res.history || []).map((log, index) => {
          const { timestamp, ...rest } = log;
          let mergedRest = { ...rest };

          // Merge payload and location data
          if (log.payload) {
            try {
              const extra = typeof log.payload === 'string' ? JSON.parse(log.payload) : log.payload;
              mergedRest = { ...mergedRest, ...extra };
            } catch (e) {}
          }
          if (log.location) {
            try {
              const loc = typeof log.location === 'string' ? JSON.parse(log.location) : log.location;
              mergedRest = { ...mergedRest, ...loc };
            } catch (e) {}
          }

          // Convert all key-value pairs into an array for easier rendering.
          // Skip non-renderable keys (objects/arrays) and meta fields that aren't sensor data.
          const SKIP_KEYS = ['device_metadata', 'device_data', 'location', 'payload', 'fields', 'time', 'fullTime', 'id', 'recorded_at', 'isManual'];
          const fields = Object.entries(mergedRest)
            .filter(([key, value]) =>
              value !== null && value !== undefined && value !== '' &&
              !SKIP_KEYS.includes(key) &&
              // Drop nested objects/arrays — React can't render them as text
              (typeof value !== 'object')
            )
            .map(([key, value]) => ({
              key: key.replace(/_/g, ' ').toUpperCase(),
              value: (typeof value === 'number' && key.includes('hours'))
                ? `${value.toFixed(2)} h`
                : (typeof value === 'number' && key.includes('fuel'))
                  ? `${value.toFixed(1)} %`
                  : String(value)
            }));

          return {
            ...mergedRest, // Keep original fields for calculation (spread first so explicit keys below win)
            id: log.id || index,
            time: timestamp ? formatToIST(timestamp, 'HH:mm:ss') : '-',
            fullTime: timestamp,
            fields,
          };
        });

        // --- NEW: If no telemetry logs but manual entry exists, add a virtual summary log ---
        if (formatted.length === 0 && res.manual_entry) {
          const me = res.manual_entry;
          const manualFields = [];
          if (me.first_key_on) manualFields.push({ key: 'FIRST KEY ON', value: me.first_key_on });
          if (me.last_key_off) manualFields.push({ key: 'LAST KEY OFF', value: me.last_key_off });

          if (me.first_key_on && me.last_key_off) {
            const [h1, m1, s1] = me.first_key_on.split(':').map(Number);
            const [h2, m2, s2] = me.last_key_off.split(':').map(Number);
            const t1 = h1 + (m1 / 60) + (s1 / 3600 || 0);
            const t2 = h2 + (m2 / 60) + (s2 / 3600 || 0);
            const workingHours = Math.max(0, t2 - t1);
            manualFields.push({ key: 'WORKING HOURS', value: `${workingHours.toFixed(2)} h` });
          }

          if (me.engine_hours) manualFields.push({ key: 'ENGINE HOURS', value: `${me.engine_hours} h` });
          if (me.fuel_used) manualFields.push({ key: 'FUEL USED', value: `${me.fuel_used} L` });
          if (me.alert_type) manualFields.push({ key: 'ALERT TYPE', value: me.alert_type });
          if (me.notes) manualFields.push({ key: 'NOTES', value: me.notes });
          if (me.entered_by) manualFields.push({ key: 'ENTERED BY', value: me.entered_by });


          formatted.push({
            id: 'manual-summary',
            time: 'MANUAL DATA',
            isManual: true,
            fields: manualFields
          });
        }

        setLogs(formatted);
        calculateDailySummary(formatted); // Calculate summary stats for this day
      }
    } catch (err) {
      console.error("Failed to fetch logs for date:", err);
    } finally {
      setLoadingLogs(false);
    }
  };

  /**
   * calculateDailySummary
   * Purpose: Looks at the first and last log of the day to calculate usage.
   * @param {Array} formattedLogs - The list of logs for the day
   */
  const calculateDailySummary = (formattedLogs) => {
    if (!formattedLogs || formattedLogs.length === 0) {
      setDailySummary(null);
      return;
    }

    // Logs are DESC (newest first). So index 0 is latest, last index is earliest.
    const firstLog = formattedLogs[formattedLogs.length - 1]; // Morning/Start
    const lastLog = formattedLogs[0]; // Evening/Latest

    // Helper to safely get value from log with robust key matching
    const getVal = (log, ...keys) => {
      // 1. Direct key match
      for (const key of keys) {
        if (log[key] !== undefined && log[key] !== null) return parseFloat(log[key]);
      }

      // 2. Normalized key match (case-insensitive, space-agnostic)
      const logKeys = Object.keys(log);
      for (const key of keys) {
        const normKey = key.toLowerCase().replace(/[\s_]/g, '');
        const match = logKeys.find(k => k.toLowerCase().replace(/[\s_]/g, '') === normKey);
        if (match && log[match] !== null && log[match] !== undefined) {
          return parseFloat(log[match]);
        }
      }
      return 0;
    };

    // --- Last Message ---
    const lastMessageTime = lastLog.fullTime || null;

    // --- First Key ON & Last Key OFF ---
    let firstKeyOnTime = null;
    let lastKeyOffTime = null;
    let firstKeyOnEngineHrs = 0;
    let lastKeyOffEngineHrs = 0;

    // Scan from earliest to latest (reverse since formattedLogs is DESC)
    for (let i = formattedLogs.length - 1; i >= 0; i--) {
      const log = formattedLogs[i];
      const ewh = getVal(log, 'engine_working_hours', 'engine_hours', 'Engine Working Hours', 'Total Hours', 'Hours');
      const espeed = getVal(log, 'engine_speed', 'engine speed', 'RPM', 'Speed');
      if (ewh > 0 || espeed > 0) {
        if (!firstKeyOnTime) {
          firstKeyOnTime = log.fullTime;
          firstKeyOnEngineHrs = ewh;
        }
        lastKeyOffTime = log.fullTime;
        lastKeyOffEngineHrs = ewh;
      }
    }

    // --- Total Working Hours (wallclock: 1st key on to last key off) ---
    let totalWorkingHours = 0;
    if (firstKeyOnTime && lastKeyOffTime) {
      const diffMs = new Date(lastKeyOffTime).getTime() - new Date(firstKeyOnTime).getTime();
      totalWorkingHours = Math.max(0, diffMs / (1000 * 60 * 60)); // Convert ms to hours
    }

    // --- Total Engine On Time (engine_working_hours difference) ---
    let totalEngineOnTime = Math.max(0, lastKeyOffEngineHrs - firstKeyOnEngineHrs);

    // --- Total Fuel Used ---
    // Use the backend-calculated fuel_consumed from logDates for consistency
    // (backend uses first-4/last-4 stable readings method on raw DB data)
    let totalFuelUsed = 0;
    if (selectedDate && logDates.length > 0) {
      const dateEntry = logDates.find(d => d.date === selectedDate);
      if (dateEntry) {
        // fuel_used_l = litres (fuel-level % delta × tank capacity, manual §2.4)
        totalFuelUsed = parseFloat(dateEntry.fuel_used_l ?? dateEntry.fuel_consumed) || 0;
      }
    }

    // Count Alarms
    const alarms = [];
    formattedLogs.forEach(log => {
      Object.keys(log).forEach(key => {
        if (key !== 'fields' && (key.includes('alarm') || key.includes('alert'))) {
          if (log[key] && log[key] !== '0' && log[key] !== 0) alarms.push({ k: key, v: log[key] });
        }
      });
    });

    setDailySummary({
      lastMessage: lastMessageTime,
      firstKeyOn: firstKeyOnTime,
      lastKeyOff: lastKeyOffTime,
      totalWorkingHours,
      totalEngineOnTime,
      totalFuelUsed,
      alarmCount: alarms.length,
      totalLogs: formattedLogs.length
    });
  };

  // Handler for when a user clicks a Date in the list
  const handleDateSelect = (date) => {
    setSelectedDate(date);
    fetchLogsForDate(date);
  };

  // Handler for downloading reports
  const handleDownload = async (type) => {
    try {
      const endpoint = `/downloads/device-logs/${type}/${technicalDeviceId}?date=${selectedDate}`;
      const { blob, filename } = await apiClient.download(endpoint);

      // Create a temporary link to download the file
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (error) {
      console.error("Download failed:", error);
    }
  };

  const handleSummaryDownloadClick = (type) => {
    setDownloadType(type);
    
    // Default to current calendar month (IST)
    const today = new Date();
    const toStr = today.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
    const fromStr = `${toStr.substring(0, 8)}01`; // first day of the current month
    
    setDownloadFromDate(fromStr);
    setDownloadToDate(toStr);
    setDownloadRangeOpen(true);
  };

  const triggerDownload = async () => {
    setDownloadRangeOpen(false);
    try {
      const endpoint = `/downloads/device-summary/${downloadType}/${technicalDeviceId}?from=${downloadFromDate}&to=${downloadToDate}&template=device-log`;
      const { blob, filename } = await apiClient.download(endpoint);

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Summary Download failed:", error);
    }
  };

  // Filter the list of dates based on search
  const filteredDates = logDates.filter(d => d.date.includes(dateSearchTerm));

  // Monthly pagination grouping
  const monthsList = useMemo(() => {
    const months = new Set();
    filteredDates.forEach(d => {
      if (d.date) {
        months.add(d.date.substring(0, 7)); // YYYY-MM
      }
    });
    return Array.from(months).sort((a, b) => b.localeCompare(a)); // newest month first
  }, [filteredDates]);

  const activeMonth = monthsList[page - 1] || null;

  const activeMonthDates = useMemo(() => {
    if (!activeMonth) return [];
    return filteredDates.filter(d => d.date.startsWith(activeMonth));
  }, [filteredDates, activeMonth]);

  // Reset page when monthsList changes or search changes
  useEffect(() => {
    setPage(1);
  }, [dateSearchTerm, monthsList.length]);


  // --- 4. Data Preparation / Memoization ---

  // Minutes since IST midnight (0..1439.98) — drives the full-day 00:00→23:59 x-axis.
  const minsSinceMidnight = (ts) => {
    const [h, m, s] = formatToIST(ts, 'HH:mm:ss').split(':').map(Number);
    return (h || 0) * 60 + (m || 0) + (s || 0) / 60;
  };
  const minutesToHHMM = (v) => {
    const t = Math.max(0, Math.min(1439, Math.round(v)));
    return `${String(Math.floor(t / 60)).padStart(2, '0')}:${String(t % 60).padStart(2, '0')}`;
  };

  const generateTrendData = useCallback((params) => {
    if (vizData.length === 0 || params.length === 0) return { labels: [], datasets: [] };

    // For simplicity, we assume single day view if time range is small, otherwise show average per day
    const isSingleDay = (vizFromDate === vizToDate);

    let labels = [];
    if (isSingleDay) {
      labels = []; // single-day uses a linear time x-axis ({x,y} points), not labels
    } else {
      const groups = {};
      vizData.forEach(d => {
        const dayKey = formatToIST(d.timestamp, 'yyyy-MM-dd');
        if (!groups[dayKey]) groups[dayKey] = 1;
      });
      labels = Object.keys(groups).sort().map(day => formatToIST(new Date(day), 'dd MMM'));
    }

    const colors = [
      { border: '#3b82f6', bg: 'rgba(59, 130, 246, 0.1)' },
      { border: '#ef4444', bg: 'rgba(239, 68, 68, 0.1)' },
      { border: '#10b981', bg: 'rgba(16, 185, 129, 0.1)' },
      { border: '#f59e0b', bg: 'rgba(245, 158, 11, 0.1)' },
      { border: '#8b5cf6', bg: 'rgba(139, 92, 246, 0.1)' },
    ];

    const datasets = params.map((param, index) => {
      let dataPoints = [];
      if (isSingleDay) {
        // {x: minutes-since-midnight, y: value} so the axis can span 00:00→23:59
        dataPoints = vizData.map(d => ({ x: minsSinceMidnight(d.timestamp), y: parseFloat(d[param]) || 0 }));
      } else {
        const groups = {};
        vizData.forEach(d => {
          const dayKey = formatToIST(d.timestamp, 'yyyy-MM-dd');
          if (!groups[dayKey]) groups[dayKey] = { sum: 0, count: 0 };
          groups[dayKey].sum += (parseFloat(d[param]) || 0);
          groups[dayKey].count += 1;
        });
        dataPoints = Object.keys(groups).sort().map(day =>
          parseFloat((groups[day].sum / groups[day].count).toFixed(2))
        );
      }

      return {
        label: formatParameterName(param),
        data: dataPoints,
        borderColor: colors[index % colors.length].border,
        backgroundColor: colors[index % colors.length].bg,
        fill: params.length === 1,
        tension: isSingleDay ? 0.3 : 0,
        pointRadius: isSingleDay ? 0 : 4,
        pointHoverRadius: 6,
        borderWidth: 2,
      };
    });

    return { labels, datasets };
  }, [vizData, vizFromDate, vizToDate]);

  const leftTrendData = useMemo(() => generateTrendData(leftTrendParams), [generateTrendData, leftTrendParams]);
  const rightTrendData = useMemo(() => generateTrendData(rightTrendParams), [generateTrendData, rightTrendParams]);

  const availableVizParameters = useMemo(() => {
    if (vizData.length === 0) return [];
    const allKeys = new Set();
    vizData.forEach(item => {
      Object.keys(item).forEach(k => allKeys.add(k));
    });
    return Array.from(allKeys).filter(k => !['id', 'device_id', 'timestamp', 'latitude', 'longitude', 'lat', 'lon', 'location', 'fields', 'time', 'fullTime'].includes(k)).sort();
  }, [vizData]);

  const allTrendData = useMemo(() => {
    if (vizData.length === 0) return {};
    const result = {};
    availableVizParameters.forEach(k => {
      result[k] = generateTrendData([k]);
    });
    return result;
  }, [vizData, generateTrendData, availableVizParameters]);

  const isSingleDayViz = !!vizFromDate && vizFromDate === vizToDate;

  const miniChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false }, tooltip: { enabled: false } },
    // Linear x so single-day {x,y} (minutes) points plot; axis hidden for sparkline.
    scales: { x: { type: 'linear', display: false }, y: { display: false } },
    animation: false
  };
  const trendChartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top', labels: { boxWidth: 10, font: { size: 10, weight: 600 } } },
      tooltip: {
        mode: 'index', intersect: false,
        callbacks: isSingleDayViz ? { title: (items) => (items.length ? minutesToHHMM(items[0].parsed.x) : '') } : {},
      },
    },
    scales: {
      // Single day → full-day time axis 00:00 → 23:59; multi-day → day categories.
      x: isSingleDayViz
        ? {
            type: 'linear', min: 0, max: 1439, grid: { display: false },
            ticks: { stepSize: 120, callback: (v) => minutesToHHMM(v), font: { size: 10 } },
          }
        : { grid: { display: false } },
      y: { beginAtZero: false, grid: { color: 'rgba(0,0,0,0.05)' } },
    },
  }), [isSingleDayViz]);


  const gpsTrail = useMemo(() => {
    return vizData
      .filter(d => (d.latitude || d.lat) && (d.longitude || d.lon))
      .map(d => [parseFloat(d.latitude || d.lat), parseFloat(d.longitude || d.lon)]);
  }, [vizData]);

  // Calculate distance travelled in km using Haversine formula
  const kmTravelled = useMemo(() => {
    if (gpsTrail.length < 2) return 0;
    let total = 0;
    for (let i = 0; i < gpsTrail.length - 1; i++) {
      const p1 = gpsTrail[i];
      const p2 = gpsTrail[i + 1];
      const R = 6371; // Earth radius in km
      const dLat = (p2[0] - p1[0]) * Math.PI / 180;
      const dLon = (p2[1] - p1[1]) * Math.PI / 180;
      const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(p1[0] * Math.PI / 180) * Math.cos(p2[0] * Math.PI / 180) *
        Math.sin(dLon / 2) * Math.sin(dLon / 2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      const distance = R * c;
      // Filter out micro-movements/noise (less than 10 meters)
      if (distance > 0.01) total += distance;
    }
    return total;
  }, [gpsTrail]);

  // Combine live store device with detailed device info (priority to detail)

  // Combine live store device with detailed device info (priority to detail)
  const device = useMemo(() => deviceDetails?.device || storeDevice, [deviceDetails, storeDevice]);

  // Extract map position for reverse geocoding & display
  const mapLat = device?.latitude || device?.location?.lat || device?.lat || null;
  const mapLon = device?.longitude || device?.location?.lon || device?.lon || null;

  const [cityName, setCityName] = useState('');

  // Fetch City Name
  useEffect(() => {
    if (mapLat && mapLon) {
      const fetchCity = async () => {
        try {
          const res = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${mapLat}&lon=${mapLon}`);
          const data = await res.json();
          if (data && data.address) {
            const city = data.address.city || data.address.town || data.address.village || data.address.state_district || data.address.county || 'Unknown Location';
            setCityName(city);
          }
        } catch (e) {
          console.error("Failed to fetch city name for MEWP Detail", e);
        }
      };
      fetchCity();
    }
  }, [mapLat, mapLon]);

  // Latest telemetry payload (sensor readings like engine_hours, fuel_level, etc.)
  const latestTelemetry = useMemo(() => deviceDetails?.latestTelemetry || {}, [deviceDetails]);

  const loading = loadingDetails && !device;

  // Prepare sensor data for the table
  const sensors = useMemo(() => {
    if (!deviceDetails?.sensors) return [];
    return deviceDetails.sensors.map(sensor => {
      let displayValue = sensor.value;
      const formattedValue = formatParameterValue(sensor.id, sensor.value);
      let unit = formattedValue.replace(sensor.value, '').trim() || '--';

      if (sensor.name && sensor.name.toLowerCase() === 'time' && sensor.value) {
        const ts = Number(sensor.value);
        if (!isNaN(ts)) {
          const dateStr = ts > 20000000000 ? new Date(ts) : new Date(ts * 1000);
          displayValue = formatToIST(dateStr, 'dd MMM yyyy, hh:mm:ss a');
          unit = '';
        }
      }

      return {
        ...sensor,
        value: displayValue,
        unit: unit,
        lastUpdated: sensor.lastUpdated ? formatToIST(sensor.lastUpdated) : 'N/A'
      };
    });
  }, [deviceDetails]);

  // Filter sensors based on search
  const filteredSensors = useMemo(() => sensors.filter(s =>
    s.name.toLowerCase().includes(searchTerm.toLowerCase())
  ), [sensors, searchTerm]);

  // Prepare Utilization Graph Data
  const utilizationData = useMemo(() => {
    if (!deviceDetails?.utilization) return { labels: [], datasets: [] };
    const labels = deviceDetails.utilization.map(u => new Date(u.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }));
    // Build time-positioned segments per day: blue = inside a shift window,
    // red = extra (outside all shifts, or the whole day on Sunday). Non-rent days
    // use status colors. Supports any number of shifts (incl. ones crossing midnight).
    const mergeIvs = (ivs) => {
      const sorted = ivs.slice().sort((a, b) => a[0] - b[0]);
      const out = [];
      for (const iv of sorted) {
        if (!out.length || iv[0] > out[out.length - 1][1]) out.push([iv[0], iv[1]]);
        else out[out.length - 1][1] = Math.max(out[out.length - 1][1], iv[1]);
      }
      return out;
    };
    const daySegments = (u) => {
      const first = u.first_hour, last = u.last_hour;
      if (!(last > first)) return [];
      if (u.status !== 'RENT') {
        const c = u.status === 'MAINTENANCE' ? '#f59e0b' : u.status === 'STOCK' ? '#94a3b8' : '#3b82f6';
        return [{ range: [first, last], color: c }];
      }
      if (u.is_sunday) return [{ range: [first, last], color: '#ef4444' }]; // Sunday: all extra
      const windows = (u.shifts && u.shifts.length) ? u.shifts : [{ start: u.shift_start_hour, end: u.shift_end_hour }];
      const ivs = [];
      windows.forEach(w => {
        if (w.end > w.start) ivs.push([w.start, w.end]);
        else if (w.end < w.start) { ivs.push([w.start, 24]); ivs.push([0, w.end]); }
        else ivs.push([0, 24]);
      });
      const shiftIvs = mergeIvs(ivs);
      const segs = [];
      let cursor = first;
      for (const [s, e] of shiftIvs) {
        const lo = Math.max(s, first), hi = Math.min(e, last);
        if (hi <= first || s >= last) continue;
        if (lo > cursor) segs.push({ range: [cursor, lo], color: '#ef4444' }); // gap before shift = extra
        const blueStart = Math.max(lo, cursor);
        if (hi > blueStart) segs.push({ range: [blueStart, hi], color: '#3b82f6' });
        cursor = Math.max(cursor, hi);
      }
      if (cursor < last) segs.push({ range: [cursor, last], color: '#ef4444' }); // after last shift = extra
      return segs;
    };
    const perDay = deviceDetails.utilization.map(daySegments);
    const maxSeg = Math.max(1, ...perDay.map(s => s.length));
    const segDatasets = [];
    for (let k = 0; k < maxSeg; k++) {
      segDatasets.push({
        label: 'Hours',
        data: perDay.map(segs => (segs[k] ? segs[k].range : null)),
        backgroundColor: perDay.map(segs => (segs[k] ? segs[k].color : 'rgba(0,0,0,0)')),
        borderRadius: 4,
        grouped: false,
        barPercentage: 0.5,
        order: 1,
      });
    }

    return {
      labels,
      datasets: [
        {
          label: 'Day Frames',
          data: deviceDetails.utilization.map(() => [0, 24]),
          backgroundColor: 'rgba(241, 245, 249, 0.6)',
          borderRadius: 4,
          categoryPercentage: 0.8,
          barPercentage: 0.9,
          grouped: false,
          order: 2, // drawn first (bottom)
        },
        ...segDatasets,
      ]
    };
  }, [deviceDetails]);

  // --- 5. Conditional Rendering (Navigation Guard) ---

  // Custom Loading Screen
  if (loading) return (
    <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="80vh" gap={2}>
      <Skeleton variant="circular" width={60} height={60} />
      <Typography variant="body1" color="text.secondary" fontWeight={600}>Loading Device Intelligence...</Typography>
    </Box>
  );

  // Error/Not Found Screen
  if (!device && !loading) return (
    <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="80vh" gap={2}>
      <AlertCircleIcon sx={{ fontSize: 64, color: 'warning.main' }} />
      <Typography variant="h5" fontWeight={700}>Asset Not Found</Typography>
      <Typography variant="body2" color="text.secondary">ID: {urlId}</Typography>
      <Button variant="contained" onClick={goEquipment}>Back to Equipment</Button>
    </Box>
  );

  const lastUpdateStr = device ? formatToIST(device.timestamp || device.last_update) : 'N/A';

  // --- 6. Main Render ---
  return (
    <Box p={0} sx={{ pt: 0 }} className="animate-fade-in">

      {/* --- Breadcrumbs Navigation — Equipment is the root --- */}
      <Breadcrumbs separator={<ChevronRightIcon fontSize="small" />} sx={{ mb: 1 }}>
        {cameFromDailyReport && (
          // Arrived from the Daily Report — go back to the exact report (team +
          // topic + day) that was being read, which only the history entry knows.
          <MuiLink component="button" onClick={() => navigate(-1)} underline="hover" color="inherit" sx={{ fontWeight: 600 }}>Daily Report</MuiLink>
        )}
        <MuiLink component="button" onClick={goEquipment} underline="hover" color="inherit" sx={{ fontWeight: 600 }}>Equipment</MuiLink>
        <Typography color="text.primary" sx={{ fontWeight: 700 }}>{urlId}</Typography>
        {deepDate && (
          <Typography color="text.primary" sx={{ fontWeight: 700 }}>{deepDate}</Typography>
        )}
      </Breadcrumbs>

      {/* --- Top Information Card (Identity & Metrics) --- */}
      <Card sx={{ mb: 1, borderRadius: '16px' }}>
        <CardContent sx={{ p: '20px !important' }}>
          {/* flexWrap is a safety valve only: with the compact viz filters the bar
              fits one line on laptop widths; on very narrow windows it wraps
              instead of pushing the Details button off-screen. */}
          <Box display="flex" alignItems="center" gap={3} sx={{ flexWrap: 'wrap', rowGap: 1.5 }}>

            {/* 1. Machine Image */}
            <Box sx={{ width: 80, height: 80, bgcolor: 'rgba(37,99,235,0.06)', borderRadius: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 1, border: '1px solid', borderColor: 'rgba(37,99,235,0.12)', flexShrink: 0 }}>
              <Box component="img" src="/images/mewp_machine.png" sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} onError={(e) => { e.target.src = "https://via.placeholder.com/200?text=UNIT"; }} />
            </Box>

            {/* 2. Identity Text (Name & Status) */}
            <Box>
              <Box display="flex" alignItems="center" gap={1.5}>
                <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                  {device?.serial_number || device?.equipment_id || device?.name || urlId}
                </Typography>
                {/* Sinoboom machines are synced from Sinoboom's i-Link cloud —
                    alarms + GPS at alarm moments; usage telemetry (hours, fuel)
                    arrives with i-Link's Q4 release. Say so, so an empty sensor
                    panel reads as "not shipped yet", not "broken". */}
                {String(device?.brand || '').toLowerCase() === 'sinoboom' && (
                  <Tooltip title="Synced from Sinoboom i-Link: alarm history + GPS at alarm moments. Engine hours, work hours and fuel arrive with Sinoboom's Q4 i-Link release.">
                    <Chip label="Sinoboom i-Link" size="small" color="info" variant="outlined"
                      sx={{ height: 20, fontSize: '0.65rem', fontWeight: 800, borderRadius: '6px' }} />
                  </Tooltip>
                )}
                {/* Status badge — changes based on context */}
                {view !== 'viz' && (
                  selectedDate && dailySummary ? (
                    /* History mode with date selected — show timestamps */
                    <Box display="flex" flexDirection="column" gap={0.5}>
                      <Chip
                        icon={<ClockIcon sx={{ fontSize: '12px !important' }} />}
                        label={`Last Msg: ${dailySummary.lastMessage ? formatToIST(dailySummary.lastMessage, 'hh:mm:ss a') : 'N/A'}`}
                        size="small"
                        sx={{ height: 20, fontSize: '0.6rem', fontWeight: 700, borderRadius: '6px', bgcolor: 'grey.100' }}
                      />
                      <Chip
                        icon={<KeyIcon sx={{ fontSize: '12px !important', color: 'info.main' }} />}
                        label={`1st Key ON: ${dailySummary.firstKeyOn ? formatToIST(dailySummary.firstKeyOn, 'hh:mm:ss a') : 'N/A'}`}
                        size="small"
                        color="info"
                        variant="outlined"
                        sx={{ height: 20, fontSize: '0.6rem', fontWeight: 700, borderRadius: '6px' }}
                      />
                      <Chip
                        icon={<StopIcon sx={{ fontSize: '12px !important', color: 'error.main' }} />}
                        label={`Last Key OFF: ${dailySummary.lastKeyOff ? formatToIST(dailySummary.lastKeyOff, 'dd MMM yyyy, hh:mm:ss a') : 'N/A'}`}
                        size="small"
                        color="error"
                        variant="outlined"
                        sx={{ height: 20, fontSize: '0.6rem', fontWeight: 700, borderRadius: '6px' }}
                      />
                    </Box>
                  ) : (
                    /* Live mode — real-time status badge (Socket.IO MQTT stream) */
                    deviceLiveStatus.loading && !liveFrame ? (
                      <Chip label="Checking..." size="small" sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, borderRadius: '6px' }} />
                    ) : (
                      <LiveStatusBadge
                        status={liveStatusCode}
                        lastSeen={liveLastSeen}
                        size="small"
                        sx={{ height: 22, fontSize: '0.7rem' }}
                      />
                    )
                  )
                )}

                {/* Asset lifecycle status (On Rent / De-hired / On Service / ...) */}
                <Tooltip title={canChangeStatus ? 'Click to change machine status' : 'Machine status'}>
                  <Chip
                    label={statusLabel(device?.status)}
                    color={statusColor(device?.status)}
                    size="small"
                    onClick={canChangeStatus ? () => setStatusDialogOpen(true) : undefined}
                    sx={{ height: 22, fontSize: '0.7rem', fontWeight: 800, borderRadius: '6px', cursor: canChangeStatus ? 'pointer' : 'default' }}
                  />
                </Tooltip>
                <Tooltip title="Status history">
                  <IconButton size="small" onClick={() => setStatusHistoryOpen(true)}>
                    <HistoryIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mt={0.5}>
                {[device?.product_category, device?.device_type].filter(Boolean).join(' • ')}
              </Typography>
              <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem', fontWeight: 600 }}>
                ID: {technicalDeviceId}{device?.brand || device?.model_number ? ` • ${[device?.brand, device?.model_number].filter(Boolean).join(' • ')}` : ''}
              </Typography>
            </Box>

            {/* Machine lifecycle status change dialog (permission-gated) */}
            <ChangeStatusDialog
              open={statusDialogOpen}
              device={device || {}}
              currentStatus={device?.status}
              onClose={() => setStatusDialogOpen(false)}
              onSuccess={() => {
                fetchDetails();
                fetchStatusHistory();
              }}
            />

            {/* Status history timeline */}
            <Dialog open={statusHistoryOpen} onClose={() => setStatusHistoryOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '20px' } }}>
              <DialogTitle sx={{ fontWeight: 800 }}>
                Status History
                <Typography variant="body2" color="text.secondary">{machineLabel(device, urlId)}</Typography>
              </DialogTitle>
              <DialogContent>
                {statusHistory.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">No status changes recorded yet.</Typography>
                ) : (
                  <Stack spacing={1.5} sx={{ mt: 1 }}>
                    {statusHistory.map(h => (
                      <Box key={h.id} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: '12px' }}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Chip label={statusLabel(h.from_status || 'UNKNOWN')} size="small" sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700 }} />
                          <Typography variant="caption">→</Typography>
                          <Chip label={statusLabel(h.to_status)} color={statusColor(h.to_status)} size="small" sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700 }} />
                          <Box flexGrow={1} />
                          <Typography variant="caption" color="text.secondary">
                            {h.created_at ? formatToIST(h.created_at, 'dd MMM yyyy, hh:mm a') : ''}
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                          {h.changed_by_name ? `By ${h.changed_by_name}` : 'System'}
                          {h.rental_contract_id ? ` • Contract ${h.rental_contract_id}` : ''}
                          {h.service_ticket_id ? ` • Service Ticket #${h.service_ticket_id}` : ''}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                )}
              </DialogContent>
              <DialogActions sx={{ p: 2 }}>
                <Button onClick={() => setStatusHistoryOpen(false)} sx={{ fontWeight: 700, textTransform: 'none', borderRadius: '10px' }}>Close</Button>
              </DialogActions>
            </Dialog>

            {/* Vertical Divider */}
            <Divider orientation="vertical" flexItem sx={{ mx: 1, height: 40, alignSelf: 'center' }} />

            {/* 3. Metrics Strip OR Viz Filters */}
            <Box display="flex" alignItems="center" gap={view === 'viz' ? 1.5 : 4} flexGrow={1} justifyContent={view === 'viz' ? "flex-end" : "flex-start"} sx={{ minWidth: 0 }}>
              {view === 'viz' ? (
                /* Viz Mode: Top Bar Filters */
                <>
                  <Box sx={{ minWidth: 150, maxWidth: 220, flexShrink: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                      <Typography variant="overline" sx={{ fontWeight: 800, color: 'text.secondary', lineHeight: 1 }}>LOG SELECTION</Typography>
                      <Tooltip title={stfEnabled ? 'STF on — scroll or press ↑/↓ over the box to change the frame' : 'STF — scroll to change the time frame'}>
                        <IconButton size="small" onClick={() => setStfEnabled(v => !v)}
                          sx={{ p: 0.2, color: stfEnabled ? '#2563eb' : 'text.disabled', bgcolor: stfEnabled ? 'rgba(37,99,235,0.12)' : 'transparent', '&:hover': { bgcolor: 'rgba(37,99,235,0.08)' } }}>
                          <SwapVertIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <Box
                      ref={stfRef}
                      onKeyDownCapture={stfEnabled ? (e) => {
                        if (e.key === 'ArrowDown') { e.preventDefault(); e.stopPropagation(); stepFrame(1); }
                        else if (e.key === 'ArrowUp') { e.preventDefault(); e.stopPropagation(); stepFrame(-1); }
                      } : undefined}
                      sx={{ borderRadius: '12px', ...(stfEnabled && { boxShadow: '0 0 0 2px rgba(37,99,235,0.45)' }) }}
                    >
                    <Autocomplete
                      id="time-selector"
                      options={[{ isAll: true }, ...vizData]}
                      getOptionLabel={(option) => option.isAll ? "All Logs" : option.timestamp ? formatToIST(option.timestamp, 'hh:mm:ss a') : 'Unknown'}
                      value={selectedVizTime || { isAll: true }}
                      isOptionEqualToValue={(option, value) => {
                        if (option.isAll && value.isAll) return true;
                        if (option.id === value.id) return true;
                        return false;
                      }}
                      onChange={(event, newValue) => {
                        if (!newValue || newValue.isAll) setSelectedVizTime(null);
                        else setSelectedVizTime(newValue);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          size="small"
                          placeholder="Search time..."
                          sx={{ borderRadius: '12px', bgcolor: 'white' }}
                        />
                      )}
                    />
                    </Box>
                  </Box>

                  <Box sx={{ minWidth: 210, maxWidth: 310, flexGrow: 1, flexShrink: 1 }}>
                    <Typography variant="overline" sx={{ fontWeight: 800, color: 'text.secondary', display: 'block', mb: 0.5 }}>DATES</Typography>
                    <Stack direction="row" spacing={1}>
                      <TextField
                        type="date"
                        size="small"
                        value={vizFromDate}
                        onChange={(e) => setVizFromDate(e.target.value)}
                        sx={{ flexGrow: 1, '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: 'white' } }}
                      />
                      <TextField
                        type="date"
                        size="small"
                        value={vizToDate}
                        onChange={(e) => setVizToDate(e.target.value)}
                        sx={{ flexGrow: 1, '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: 'white' } }}
                      />
                    </Stack>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      onClick={handleApplyViz}
                      disabled={loadingViz || !vizFromDate || !vizToDate}
                      startIcon={loadingViz ? <CircularProgress size={14} color="inherit" /> : <CheckIcon sx={{ fontSize: 16 }} />}
                      sx={{
                        borderRadius: '10px',
                        fontWeight: 800,
                        textTransform: 'none',
                        px: 1.5,
                        py: 0.6,
                        flexShrink: 0,
                        whiteSpace: 'nowrap',
                        boxShadow: vizApplied ? 'none' : '0 0 0 3px rgba(59, 130, 246, 0.4)',
                        animation: !vizApplied ? 'pulseApply 2s ease-in-out infinite' : 'none',
                        '@keyframes pulseApply': {
                          '0%, 100%': { boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.3)' },
                          '50%': { boxShadow: '0 0 0 6px rgba(59, 130, 246, 0.1)' },
                        },
                      }}
                    >
                      {loadingViz ? 'Loading...' : 'Apply'}
                    </Button>
                  </Box>
                </>
              ) : selectedDate && dailySummary ? (
                /* Mode A: Daily Stats (When viewing specific history date) */
                <>
                  <Box>
                    <Stack direction="row" alignItems="center" gap={1} mb={0.5}>
                      <ClockIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                      <Typography variant="caption" fontWeight={700} color="text.secondary">TOTAL WORKING HRS</Typography>
                    </Stack>
                    <Typography variant="h6" fontWeight={800} lineHeight={1}>{dailySummary.totalWorkingHours.toFixed(1)} <Typography component="span" variant="caption" color="text.secondary" fontWeight={600}>h</Typography></Typography>
                  </Box>

                  <Divider orientation="vertical" flexItem sx={{ height: 30, alignSelf: 'center' }} />

                  <Box>
                    <Stack direction="row" alignItems="center" gap={1} mb={0.5}>
                      <LiveIcon sx={{ fontSize: 16, color: 'success.main' }} />
                      <Typography variant="caption" fontWeight={700} color="text.secondary">ENGINE ON TIME</Typography>
                    </Stack>
                    <Typography variant="h6" fontWeight={800} lineHeight={1}>{dailySummary.totalEngineOnTime.toFixed(1)} <Typography component="span" variant="caption" color="text.secondary" fontWeight={600}>h</Typography></Typography>
                  </Box>

                  <Divider orientation="vertical" flexItem sx={{ height: 30, alignSelf: 'center' }} />

                  <Box>
                    <Stack direction="row" alignItems="center" gap={1} mb={0.5}>
                      <FuelIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                      <Typography variant="caption" fontWeight={700} color="text.secondary">FUEL USED</Typography>
                    </Stack>
                    <Typography variant="h6" fontWeight={800} lineHeight={1}>{dailySummary.totalFuelUsed.toFixed(1)} <Typography component="span" variant="caption" color="text.secondary" fontWeight={600}>L</Typography></Typography>
                  </Box>

                  {/* Download Options for this day */}
                  <Stack spacing={1} ml={2}>
                    <Button
                      variant="outlined"
                      size="small"
                      color="error"
                      startIcon={<PictureAsPdf sx={{ fontSize: 14 }} />}
                      onClick={() => handleDownload('pdf')}
                      sx={{ borderRadius: '6px', py: 0.2, fontSize: '10px', fontWeight: 700, minWidth: 80, height: 24 }}
                    >
                      PDF
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      color="success"
                      startIcon={<TableView sx={{ fontSize: 14 }} />}
                      onClick={() => handleDownload('excel')}
                      sx={{ borderRadius: '6px', py: 0.2, fontSize: '10px', fontWeight: 700, minWidth: 80, height: 24 }}
                    >
                      EXCEL
                    </Button>
                  </Stack>
                </>
              ) : (
                /* Mode B: Standard Live Mode — the two meters read as ONE
                   column (engine hours above fuel), not side by side. */
                <Stack spacing={1.25}>
                  <Box display="flex" alignItems="center" gap={1.25}>
                    <Avatar variant="rounded" sx={{ width: 34, height: 34, borderRadius: '9px', bgcolor: 'rgba(37,99,235,0.10)', color: 'primary.main' }}>
                      <ClockIcon sx={{ fontSize: 19 }} />
                    </Avatar>
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: '9.5px', letterSpacing: '.06em', textTransform: 'uppercase', color: 'text.disabled' }}>Engine hrs</Typography>
                      <Typography sx={{ fontFamily: 'Outfit', fontWeight: 800, fontSize: 17, lineHeight: 1, mt: '2px' }}>
                        {selectedDate ? (
                          selectedDateManualEntry?.engine_hours ||
                          dailySummary?.engineWorkingHours?.toFixed(1) ||
                          dailySummary?.totalEngineOnTime?.toFixed(1) ||
                          '0'
                        ) : (
                          deviceDetails?.resolvedEngineHours || latestTelemetry?.engine_working_hours || latestTelemetry?.engine_hours || device?.engine_hours || '0'
                        )} <Typography component="span" sx={{ fontSize: 11, fontWeight: 500, color: 'text.disabled' }}>h</Typography>
                      </Typography>
                    </Box>
                  </Box>

                  <Box display="flex" alignItems="center" gap={1.25}>
                    <Avatar variant="rounded" sx={{ width: 34, height: 34, borderRadius: '9px', bgcolor: 'warning.light', color: 'warning.dark' }}>
                      <FuelIcon sx={{ fontSize: 19 }} />
                    </Avatar>
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: '9.5px', letterSpacing: '.06em', textTransform: 'uppercase', color: 'text.disabled' }}>Fuel</Typography>
                      <Typography sx={{ fontFamily: 'Outfit', fontWeight: 800, fontSize: 17, lineHeight: 1, mt: '2px' }}>
                        {selectedDate ? (
                          selectedDateManualEntry?.fuel_used !== undefined ? `${selectedDateManualEntry.fuel_used} L` :
                            dailySummary?.totalFuelUsed ? `${dailySummary.totalFuelUsed.toFixed(1)} L` :
                              '0 L'
                        ) : (
                          `${latestTelemetry?.fuel_level || latestTelemetry?.adc_level || device?.fuel_level || '0'} %`
                        )}
                      </Typography>
                    </Box>
                  </Box>
                </Stack>
              )}
            </Box>

            {/* 4. Action Buttons (Live vs History). flexShrink 0 + nowrap keeps
                every button (incl. Details) fully visible; the compact filters
                above make the whole bar fit on laptop widths. */}
            {/* Two columns of two: Live Data over History, Visualization over
                Details — half the width of a single row, and the pairs read
                together (what is happening now / how to look deeper). */}
            <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
              <Stack spacing={1}>
                <Button
                  size="small"
                  fullWidth
                  variant={view === 'dashboard' ? 'contained' : 'outlined'}
                  color={view === 'dashboard' ? 'primary' : 'inherit'}
                  onClick={() => { setView('dashboard'); setSelectedDate(null); }}
                  startIcon={<LiveIcon />}
                  sx={{ borderRadius: '10px', fontWeight: 700, textTransform: 'none', borderColor: 'divider', whiteSpace: 'nowrap', px: 1.25, justifyContent: 'flex-start' }}
                >
                  Live Data
                </Button>
                <Button
                  size="small"
                  fullWidth
                  variant={view === 'logs' ? 'contained' : 'outlined'}
                  color={view === 'logs' ? 'primary' : 'inherit'}
                  onClick={() => setView('logs')}
                  startIcon={<HistoryIcon />}
                  sx={{ borderRadius: '10px', fontWeight: 700, textTransform: 'none', borderColor: 'divider', whiteSpace: 'nowrap', px: 1.25, justifyContent: 'flex-start' }}
                >
                  History
                </Button>
              </Stack>
              <Stack spacing={1}>
                <Button
                  size="small"
                  fullWidth
                  variant={view === 'viz' ? 'contained' : 'outlined'}
                  color={view === 'viz' ? 'primary' : 'inherit'}
                  onClick={() => setView('viz')}
                  startIcon={<AnalyticsIcon />}
                  sx={{ borderRadius: '10px', fontWeight: 700, textTransform: 'none', borderColor: 'divider', whiteSpace: 'nowrap', px: 1.25, justifyContent: 'flex-start' }}
                >
                  Visualization
                </Button>
                {/* Details = the SERVICE view of this machine (last service, next
                    maintenance due, the service-A..H checklist and schedule) —
                    the same page /service-machine/:id shows, rendered here. */}
                <Button
                  size="small"
                  fullWidth
                  variant="contained"
                  color="primary"
                  className={view === 'service' ? undefined : 'gradient-primary'}
                  endIcon={<ChevronRightIcon />}
                  onClick={() => setView('service')}
                  sx={{ borderRadius: '10px', fontWeight: 700, textTransform: 'none', px: 1.5, whiteSpace: 'nowrap', justifyContent: 'flex-start' }}
                >
                  Details
                </Button>
              </Stack>
            </Stack>

          </Box>
        </CardContent>
      </Card>

      {/* --- Conditional Interface: Service details / Logs / Dashboard / Viz --- */}
      {view === 'service' ?
        <ServiceMachineDetailPage embedded equipmentId={device?.equipment_id || urlId} />
      : view === 'logs' ?
        <React.Fragment>
          {!selectedDate ? (
            // State: Show List of Dates
            <Card className="animate-fade-in" sx={{ borderRadius: '24px', overflow: 'hidden' }}>
              <Box p={3} borderBottom="1px solid" borderColor="divider" display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="h6" fontWeight={800}>Machine Logs</Typography>
                  <Typography variant="body2" color="text.secondary">Select a date to view detailed logs for {urlId}</Typography>
                </Box>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Tooltip title="Download PDF Summary">
                    <IconButton onClick={() => handleSummaryDownloadClick('pdf')} size="small" sx={{ color: 'error.main', bgcolor: 'error.50', '&:hover': { bgcolor: 'error.100' } }}>
                      <PictureAsPdf fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Download Excel Summary">
                    <IconButton onClick={() => handleSummaryDownloadClick('excel')} size="small" sx={{ color: 'success.main', bgcolor: 'success.50', '&:hover': { bgcolor: 'success.100' } }}>
                      <TableView fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<NoteAddIcon />}
                    onClick={() => { setManualLogEditId(null); setManualLogForm({ log_date: '', first_key_on: '', last_key_off: '', engine_hours: '', fuel_used: '', alert_type: '', alert_message: '', notes: '', entered_by: '' }); setManualLogOpen(true); }}
                    sx={{ borderRadius: '12px', fontWeight: 700, textTransform: 'none', bgcolor: '#f59e0b', '&:hover': { bgcolor: '#d97706' } }}
                  >
                    Add Missed Data
                  </Button>
                  <TextField
                    size="small"
                    placeholder="Filtered by date..."
                    value={dateSearchTerm}
                    onChange={(e) => { setDateSearchTerm(e.target.value); setPage(1); }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                      sx: { borderRadius: '12px', bgcolor: 'grey.50' }
                    }}
                  />
                </Stack>
              </Box>
              <TableContainer sx={{ minHeight: 400 }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell sx={{ fontWeight: 700, pl: 4 }}>DATE</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>WORKING HRS</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>DAILY ENGINE HRS</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>STARTING ENGINE HRS</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>ENDING ENGINE HRS</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>FUEL USED</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>1st ENGINE ON</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>LAST ENGINE OFF</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>ENTRIES</TableCell>
                      <TableCell align="right" sx={{ pr: 4 }}></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loadingLogs && <TableRow><TableCell colSpan={10} align="center">Loading...</TableCell></TableRow>}
                    {!loadingLogs && filteredDates.length === 0 && <TableRow><TableCell colSpan={10} align="center">No logs found.</TableCell></TableRow>}
                    {activeMonthDates.map((item) => (
                      <TableRow key={item.date} hover onClick={() => handleDateSelect(item.date)} sx={{ cursor: 'pointer', '&:last-child td, &:last-child th': { border: 0 } }}>
                        <TableCell sx={{ pl: 4 }}>
                          <Box display="flex" alignItems="center" gap={2}>
                            <Avatar sx={{ bgcolor: 'primary.50', color: 'primary.main', borderRadius: '10px', width: 40, height: 40 }} variant="rounded">
                              <CalendarToday fontSize="small" />
                            </Avatar>
                            <Box>
                              <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.95rem' }}>
                                {new Date(item.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" fontWeight={500}>
                                {new Date(item.date).toLocaleDateString('en-GB', { weekday: 'long' })}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={800} color="primary.main">{item.working_hours || '0.00'} h</Typography>
                        </TableCell>
                        {(() => {
                          /* Opening / closing meter for THIS day: the engine-hours
                             value on the day's first and last frames that carry one
                             (server: start_engine_hours / end_engine_hours).
                             Previously "starting" was taken from the PREVIOUS day's
                             row and "ending" from this day's max, so any day without
                             an engine-hours frame read 0.00 and poisoned both its own
                             ending and the next day's starting — which is how a day
                             came to show 577 h of engine time. */
                          const startEH = item.start_engine_hours != null ? parseFloat(item.start_engine_hours) : null;
                          const endEH = item.end_engine_hours != null ? parseFloat(item.end_engine_hours) : null;
                          const dailyEH = (startEH != null && endEH != null) ? Math.max(0, endEH - startEH) : null;
                          const show = (v) => (v == null ? '–' : `${v.toFixed(2)} h`);
                          return (
                            <React.Fragment>
                              <TableCell>
                                <Typography variant="body2" fontWeight={800} color="secondary.main">
                                  {show(dailyEH)}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" fontWeight={800} color="text.secondary">
                                  {show(startEH)}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" fontWeight={800}>{show(endEH)}</Typography>
                              </TableCell>
                            </React.Fragment>
                          );
                        })()}
                        <TableCell>
                          <Typography variant="body2" fontWeight={800} color="error.main">{item.fuel_used_l ?? item.fuel_consumed ?? '0.0'} L</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={800} color="success.main">
                            {(() => {
                              const val = item.first_engine_on;
                              if (!val) return '-';
                              if (val.includes('T') || val.includes('-')) return formatToIST(val, 'hh:mm a');
                              // Handle "HH:mm:ss" from manual entry
                              try {
                                const [h, m] = val.split(':');
                                const hours = parseInt(h);
                                const ampm = hours >= 12 ? 'PM' : 'AM';
                                const h12 = hours % 12 || 12;
                                return `${String(h12).padStart(2, '0')}:${m} ${ampm}`;
                              } catch (e) { return val; }
                            })()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={800} color="error.main">
                            {(() => {
                              const val = item.last_engine_off;
                              if (!val) return '-';
                              if (val.includes('T') || val.includes('-')) return formatToIST(val, 'hh:mm a');
                              // Handle "HH:mm:ss" from manual entry
                              try {
                                const [h, m] = val.split(':');
                                const hours = parseInt(h);
                                const ampm = hours >= 12 ? 'PM' : 'AM';
                                const h12 = hours % 12 || 12;
                                return `${String(h12).padStart(2, '0')}:${m} ${ampm}`;
                              } catch (e) { return val; }
                            })()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1} alignItems="center">
                            {item.has_auto && (
                              <Chip
                                label={`${item.count} Entries`}
                                size="small"
                                color="default"
                                sx={{ fontWeight: 600, bgcolor: 'grey.100', borderRadius: '6px' }}
                              />
                            )}
                            {item.has_manual && (
                              <Chip
                                label="Manual Entry"
                                size="small"
                                sx={{ fontWeight: 700, bgcolor: '#fef3c7', color: '#92400e', border: '1px solid #f59e0b', borderRadius: '6px' }}
                              />
                            )}
                          </Stack>
                        </TableCell>

                        <TableCell align="right" sx={{ pr: 4 }}><ChevronRightIcon color="action" /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination Controls */}
              {!loadingLogs && monthsList.length > 0 && (
                <Box p={3} display="flex" flexDirection="column" alignItems="center" gap={1.5}>
                  <Typography variant="subtitle2" color="primary.main" fontWeight={800} sx={{ letterSpacing: '0.5px' }}>
                    Month: {activeMonth ? new Date(activeMonth + '-02').toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }) : ''}
                  </Typography>
                  {monthsList.length > 1 && (
                    <Pagination
                      count={monthsList.length}
                      page={page}
                      onChange={handlePageChange}
                      color="primary"
                      showFirstButton
                      showLastButton
                      shape="rounded"
                      size="large"
                    />
                  )}
                </Box>
              )}
            </Card>
          ) : (
            // State: Show Details for ONE Date
            <Box className="animate-fade-in">
              <Button startIcon={<ArrowBack />} onClick={() => { setSelectedDate(null); setLogPage(1); setLogSearchTerm(''); }} sx={{ mb: 2, fontWeight: 700 }}>Back</Button>

              <Card sx={{ borderRadius: '24px' }}>
                {selectedDateManualEntry && (
                  <Box p={3} bgcolor="#fffbeb" borderBottom="1px solid" borderColor="#fde68a">
                    <Stack direction="row" alignItems="center" gap={1} mb={2}>
                      <NoteAddIcon sx={{ color: '#92400e' }} />
                      <Typography variant="subtitle2" fontWeight={800} color="#92400e">MANUAL LOG SUMMARY</Typography>
                      <Chip label="Entered Data" size="small" sx={{ bgcolor: '#fef3c7', color: '#92400e', fontWeight: 700, height: 20, fontSize: '10px' }} />
                    </Stack>
                    <Grid container spacing={3}>
                      <Grid item xs={6} md={2}>
                        <Typography variant="caption" fontWeight={700} color="text.secondary" display="block">FIRST KEY ON</Typography>
                        <Typography variant="body1" fontWeight={800}>{selectedDateManualEntry.first_key_on || '-'}</Typography>
                      </Grid>
                      <Grid item xs={6} md={1.5}>
                        <Typography variant="caption" fontWeight={700} color="text.secondary" display="block">LAST KEY OFF</Typography>
                        <Typography variant="body1" fontWeight={800}>{selectedDateManualEntry.last_key_off || '-'}</Typography>
                      </Grid>
                      {selectedDateManualEntry?.first_key_on && selectedDateManualEntry?.last_key_off && (
                        <Grid item xs={6} md={1.5}>
                          <Typography variant="caption" fontWeight={700} color="text.secondary" display="block">WORKING HRS</Typography>
                          <Typography variant="body1" fontWeight={800}>
                            {(() => {
                              const [h1, m1, s1 = 0] = selectedDateManualEntry.first_key_on.split(':').map(Number);
                              const [h2, m2, s2 = 0] = selectedDateManualEntry.last_key_off.split(':').map(Number);
                              const t1 = h1 + (m1 / 60) + (s1 / 3600);
                              const t2 = h2 + (m2 / 60) + (s2 / 3600);
                              return Math.max(0, t2 - t1).toFixed(2);
                            })()} h

                          </Typography>
                        </Grid>
                      )}

                      <Grid item xs={6} md={2}>
                        <Typography variant="caption" fontWeight={700} color="text.secondary" display="block">ENGINE HOURS</Typography>
                        <Typography variant="body1" fontWeight={800}>{selectedDateManualEntry.engine_hours || '-'} h</Typography>
                      </Grid>
                      <Grid item xs={6} md={2}>
                        <Typography variant="caption" fontWeight={700} color="text.secondary" display="block">FUEL USED</Typography>
                        <Typography variant="body1" fontWeight={800}>{selectedDateManualEntry.fuel_used ? `${selectedDateManualEntry.fuel_used} L` : '-'}</Typography>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Typography variant="caption" fontWeight={700} color="text.secondary" display="block">ALERTS & NOTES</Typography>
                        <Box display="flex" gap={1} alignItems="center">
                          {selectedDateManualEntry.alert_type && <Chip label={selectedDateManualEntry.alert_type} size="small" color="warning" sx={{ height: 20, fontSize: '10px', fontWeight: 700 }} />}
                          <Typography variant="body2" fontWeight={600} noWrap>{selectedDateManualEntry.notes || 'No notes provided'}</Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                )}
                <Box p={2} borderBottom="1px solid" borderColor="divider" display="flex" justifyContent="space-between">

                  <TextField
                    size="small"
                    placeholder={`Search in ${logs.length} logs...`}
                    value={logSearchTerm}
                    onChange={(e) => { setLogSearchTerm(e.target.value); setLogPage(1); }}
                    fullWidth
                    InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ color: 'text.disabled' }} /> }}
                  />
                </Box>
                <Box p={0} maxHeight={600} sx={{ overflowY: 'auto' }}>
                  {loadingLogs && <Box p={3} textAlign="center">Loading...</Box>}
                  {!loadingLogs && logs.length === 0 && (
                    <Box p={3} textAlign="center">
                      <Typography color="text.secondary">No logs found for this date.</Typography>
                      <Typography variant="caption" display="block">Debug: Date={selectedDate}</Typography>
                    </Box>
                  )}
                  {(() => {
                    const filteredLogs = logs.filter(l => JSON.stringify(l).toLowerCase().includes(logSearchTerm.toLowerCase()));
                    const totalLogPages = Math.ceil(filteredLogs.length / LOGS_PER_PAGE);
                    const pagedLogs = filteredLogs.slice((logPage - 1) * LOGS_PER_PAGE, logPage * LOGS_PER_PAGE);
                    return (
                      <>
                        {pagedLogs.map(log => (
                          <Box key={log.id} p={2} borderBottom="1px solid" borderColor="divider" sx={{ bgcolor: log.isManual ? '#fffbeb' : 'transparent' }}>
                            <Box display="flex" justifyContent="space-between" mb={1} alignItems="center">
                              <Chip
                                label={log.time}
                                size="small"
                                sx={{
                                  borderRadius: '4px',
                                  bgcolor: log.isManual ? '#f59e0b' : 'grey.100',
                                  color: log.isManual ? 'white' : 'inherit',
                                  fontWeight: 700,
                                  fontFamily: 'monospace'
                                }}
                              />
                              {log.isManual && (
                                <Chip label="MANUAL MISSING DATA RECOVERED" size="small" sx={{ bgcolor: '#fcd34d', color: '#92400e', fontWeight: 800, fontSize: '10px' }} />
                              )}
                            </Box>
                            <Grid container spacing={2}>
                              {log.fields.map((f, i) => (
                                <Grid item xs={6} md={log.isManual ? 3 : 2} key={i}>
                                  <Typography variant="caption" color="text.secondary" display="block" fontWeight={700}>{f.key}</Typography>
                                  <Typography variant="body2" fontWeight={800} color={log.isManual ? '#92400e' : 'text.primary'}>{f.value}</Typography>
                                </Grid>
                              ))}
                            </Grid>
                          </Box>
                        ))}
                        {totalLogPages > 1 && (
                          <Box py={2} display="flex" justifyContent="center" alignItems="center" gap={2} borderTop="1px solid" sx={{ borderColor: 'divider', position: 'sticky', bottom: 0, bgcolor: 'background.paper', zIndex: 2 }}>
                            <Typography variant="caption" color="text.secondary" fontWeight={600}>
                              Showing {((logPage - 1) * LOGS_PER_PAGE) + 1}–{Math.min(logPage * LOGS_PER_PAGE, filteredLogs.length)} of {filteredLogs.length} logs
                            </Typography>
                            <Pagination
                              count={totalLogPages}
                              page={logPage}
                              onChange={(_, p) => { setLogPage(p); }}
                              color="primary"
                              size="small"
                              showFirstButton
                              showLastButton
                              shape="rounded"
                            />
                          </Box>
                        )}
                      </>
                    );
                  })()}

                </Box>
              </Card>

            </Box >
          )}
          {/* --- Manual Log Entry Dialog --- */}
          <Dialog open={manualLogOpen} onClose={() => setManualLogOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '20px' } }}>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 800, borderBottom: 1, borderColor: 'divider' }}>
              {manualLogEditId ? 'Edit Missed Data' : 'Add Missed Data'}
              <IconButton onClick={() => setManualLogOpen(false)} size="small"><CloseIcon /></IconButton>
            </DialogTitle>
            <DialogContent sx={{ pt: '24px !important' }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth label="Date" type="date" size="small" value={manualLogForm.log_date} onChange={e => setManualLogForm(p => ({ ...p, log_date: e.target.value }))} InputLabelProps={{ shrink: true }} required />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth label="First Key On" type="time" size="small" value={manualLogForm.first_key_on} onChange={e => setManualLogForm(p => ({ ...p, first_key_on: e.target.value }))} InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth label="Last Key Off" type="time" size="small" value={manualLogForm.last_key_off} onChange={e => setManualLogForm(p => ({ ...p, last_key_off: e.target.value }))} InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth label="Engine Hours" type="number" size="small" value={manualLogForm.engine_hours} onChange={e => setManualLogForm(p => ({ ...p, engine_hours: e.target.value }))} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth label="Fuel Used (Liters)" type="number" size="small" value={manualLogForm.fuel_used} onChange={e => setManualLogForm(p => ({ ...p, fuel_used: e.target.value }))} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Alert Type</InputLabel>
                    <Select label="Alert Type" value={manualLogForm.alert_type} onChange={e => setManualLogForm(p => ({ ...p, alert_type: e.target.value }))}>
                      <MenuItem value=""><em>None</em></MenuItem>
                      <MenuItem value="DEVICE_OFFLINE">Device Offline</MenuItem>
                      <MenuItem value="LOW_UTILIZATION">Low Utilization</MenuItem>
                      <MenuItem value="BREAKDOWN">Breakdown</MenuItem>
                      <MenuItem value="MAINTENANCE">Maintenance</MenuItem>
                      <MenuItem value="BATTERY_LOW">Battery Low</MenuItem>
                      <MenuItem value="OVERHEATING">Overheating</MenuItem>
                      <MenuItem value="OTHER">Other</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth label="Alert Message" size="small" multiline rows={2} value={manualLogForm.alert_message} onChange={e => setManualLogForm(p => ({ ...p, alert_message: e.target.value }))} />
                </Grid>
                <Grid item xs={12} md={8}>
                  <TextField fullWidth label="Notes" size="small" multiline rows={2} value={manualLogForm.notes} onChange={e => setManualLogForm(p => ({ ...p, notes: e.target.value }))} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth label="Entered By" size="small" value={manualLogForm.entered_by} onChange={e => setManualLogForm(p => ({ ...p, entered_by: e.target.value }))} />
                </Grid>
              </Grid>

              {/* Existing Manual Logs Table */}
              {manualLogs.length > 0 && (
                <Box mt={4}>
                  <Typography variant="subtitle2" fontWeight={800} mb={1}>Existing Manual Entries ({manualLogs.length})</Typography>
                  <TableContainer sx={{ maxHeight: 300, border: '1px solid', borderColor: 'divider', borderRadius: '12px' }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'grey.50' }}>
                          <TableCell sx={{ fontWeight: 700, fontSize: '11px' }}>DATE</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: '11px' }}>KEY ON</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: '11px' }}>KEY OFF</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: '11px' }}>ENGINE HRS</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: '11px' }}>FUEL</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: '11px' }}>ALERT</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: '11px' }}>NOTES</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: '11px' }} align="right">ACTIONS</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {manualLogs.map(log => (
                          <TableRow key={log.id} hover>
                            <TableCell sx={{ fontSize: '12px', fontWeight: 600 }}>{log.log_date ? new Date(log.log_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}</TableCell>
                            <TableCell sx={{ fontSize: '12px' }}>{log.first_key_on || '-'}</TableCell>
                            <TableCell sx={{ fontSize: '12px' }}>{log.last_key_off || '-'}</TableCell>
                            <TableCell sx={{ fontSize: '12px' }}>{log.engine_hours || '-'}</TableCell>
                            <TableCell sx={{ fontSize: '12px' }}>{log.fuel_used ? `${log.fuel_used}%` : '-'}</TableCell>
                            <TableCell>{log.alert_type ? <Chip label={log.alert_type} size="small" color="warning" sx={{ fontSize: '10px', height: 20 }} /> : '-'}</TableCell>
                            <TableCell sx={{ fontSize: '11px', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{log.notes || '-'}</TableCell>
                            <TableCell align="right">
                              <IconButton size="small" onClick={() => handleEditManualLog(log)}><EditIcon fontSize="small" /></IconButton>
                              <IconButton size="small" color="error" onClick={() => handleDeleteManualLog(log.id)}><DeleteIcon fontSize="small" /></IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}>
              <Button onClick={() => setManualLogOpen(false)} sx={{ fontWeight: 700, textTransform: 'none' }}>Cancel</Button>
              <Button variant="contained" onClick={handleSaveManualLog} disabled={manualLogSaving || !manualLogForm.log_date} sx={{ fontWeight: 700, textTransform: 'none', borderRadius: '10px', px: 3 }}>
                {manualLogSaving ? 'Saving...' : (manualLogEditId ? 'Update Entry' : 'Save Entry')}
              </Button>
            </DialogActions>
          </Dialog>

          {/* --- Download Date Range Dialog --- */}
          <Dialog 
            open={downloadRangeOpen} 
            onClose={() => setDownloadRangeOpen(false)} 
            PaperProps={{ sx: { borderRadius: '24px', p: 1 } }} 
            maxWidth="xs" 
            fullWidth
          >
            <DialogTitle sx={{ fontWeight: 800, pb: 1 }}>Download Usage Summary</DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Select the date range for the usage logs you want to export.
              </Typography>
              <Stack spacing={2.5}>
                <TextField
                  label="From Date"
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={downloadFromDate}
                  onChange={(e) => setDownloadFromDate(e.target.value)}
                  InputProps={{ sx: { borderRadius: '12px' } }}
                />
                <TextField
                  label="To Date"
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={downloadToDate}
                  onChange={(e) => setDownloadToDate(e.target.value)}
                  InputProps={{ sx: { borderRadius: '12px' } }}
                />
              </Stack>
            </DialogContent>
            <DialogActions sx={{ p: 2.5, gap: 1 }}>
              <Button onClick={() => setDownloadRangeOpen(false)} sx={{ fontWeight: 700, textTransform: 'none', borderRadius: '10px' }}>
                Cancel
              </Button>
              <Button onClick={triggerDownload} variant="contained" sx={{ fontWeight: 700, textTransform: 'none', borderRadius: '10px' }} disabled={!downloadFromDate || !downloadToDate}>
                Download
              </Button>
            </DialogActions>
          </Dialog>
        </React.Fragment>
        : view === 'dashboard' ?
          <Stack spacing={3} className="animate-fade-in">
            {/* Row 1: Location · Live Data (rig + readings) · Activity.
                The machine-position rig used to own a full-width row of its own
                above this; it cost a screenful of height to show one drawing, so
                it now sits inside the Live Data card beside the readings it is
                posed from — same picture, no extra row. */}
            <Grid container spacing={3}>

            {/* Left Column: Location Map */}
            <Grid item xs={12} md={3} lg={3}>
              <Stack spacing={3}>

                {/* Map Card */}
                <Card sx={{ height: 520, display: 'flex', flexDirection: 'column' }}>
                  <Box p={2} display="flex" justifyContent="space-between" alignItems="center" sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <MapIcon color="primary" />
                      <Typography variant="subtitle2" fontWeight={800} sx={{ display: 'flex', alignItems: 'center' }}>
                        Location
                        {cityName && (
                          <Chip 
                              label={cityName} 
                              size="small" 
                              color="primary" 
                              variant="outlined" 
                              sx={{ fontWeight: 800, ml: 1.5, height: '22px', fontSize: '11px', bgcolor: 'primary.50' }} 
                          />
                        )}
                      </Typography>
                    </Box>
                    {(device?.latitude || device?.location?.lat) && (
                      <Stack direction="row" spacing={1} bgcolor="grey.100" p={0.5} borderRadius="10px">
                        <Button size="small" onClick={() => setMapType('satellite')} variant={mapType === 'satellite' ? 'contained' : 'text'} sx={{ fontSize: '10px', minWidth: 'auto', px: 1 }}>SAT</Button>
                        <Button size="small" onClick={() => setMapType('map')} variant={mapType === 'map' ? 'contained' : 'text'} sx={{ fontSize: '10px', minWidth: 'auto', px: 1 }}>MAP</Button>
                      </Stack>
                    )}
                  </Box>
                  <Box sx={{ flexGrow: 1, position: 'relative' }}>
                    {(() => {
                      const locTime = device?.location?.timestamp || device?.timestamp || null;

                      if (!mapLat || !mapLon) {
                        // No GPS data available — show a message instead of a fake default location
                        return (
                          <Box
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            justifyContent="center"
                            height="100%"
                            bgcolor="grey.50"
                            gap={2}
                          >
                            <MapIcon sx={{ fontSize: 64, color: 'grey.300' }} />
                            <Typography variant="h6" fontWeight={700} color="text.secondary">
                              No GPS Data Available
                            </Typography>
                            <Typography variant="body2" color="text.disabled" textAlign="center" maxWidth={300}>
                              This device has not reported any location data yet.
                              GPS coordinates will appear here once the device sends location telemetry.
                            </Typography>
                          </Box>
                        );
                      }

                      // Format the location timestamp for display
                      const locationTimeStr = locTime ? formatToIST(locTime) : 'Unknown';

                      return (
                        <MapContainer center={[mapLat, mapLon]} zoom={15} style={{ height: '100%', width: '100%' }}>
                          <TileLayer url={mapType === 'satellite' ? "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}" : "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"} />
                          <Marker position={[mapLat, mapLon]}>
                            <Popup>
                              <Box sx={{ minWidth: 180 }}>
                                <Typography variant="subtitle2" fontWeight={800} gutterBottom>
                                  📍 Location
                                </Typography>
                                <Typography variant="caption" display="block" color="text.secondary">
                                  <strong>Lat:</strong> {Number(mapLat).toFixed(6)}
                                </Typography>
                                <Typography variant="caption" display="block" color="text.secondary">
                                  <strong>Lon:</strong> {Number(mapLon).toFixed(6)}
                                </Typography>
                                <Divider sx={{ my: 0.5 }} />
                                <Typography variant="caption" display="block" fontWeight={700} color="primary.main">
                                  🕐 {locationTimeStr}
                                </Typography>
                              </Box>
                            </Popup>
                          </Marker>
                        </MapContainer>
                      );
                    })()}
                    {(device?.latitude || device?.location?.lat) && (
                      <Box sx={{ position: 'absolute', bottom: 16, left: 16, zIndex: 1000 }}>
                        <Chip
                          label={`Last seen: ${device?.location?.timestamp ? formatToIST(device.location.timestamp) : 'N/A'}`}
                          size="small"
                          color="primary"
                          sx={{ fontWeight: 700, fontSize: '0.65rem', bgcolor: 'rgba(37, 99, 235, 0.9)', backdropFilter: 'blur(4px)' }}
                        />
                      </Box>
                    )}
                  </Box>
                </Card>


              </Stack>
            </Grid>

            {/* Right of the map: Live Data — the machine drawing and EVERY
                reading in the latest frame. It took the 3 columns Recent
                Activity used to hold (that card moved down to row 2), and its
                height follows its content instead of being capped at 520 with
                the readings hidden behind a scrollbar. Three of those columns
                now carry the Operator card beside it. */}
            <Grid item xs={12} md={6} lg={6}>
              <Card data-testid="live-data-card" sx={{ height: 560, display: 'flex', flexDirection: 'column' }}>
                <Box p={3} sx={{ borderBottom: 1, borderColor: 'divider' }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                    <Typography variant="h6" fontWeight={800}>Live Data</Typography>
                    <TextField
                      size="small"
                      placeholder="Search..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>, sx: { borderRadius: '12px', bgcolor: 'grey.50' } }}
                      sx={{ width: 140 }}
                    />
                  </Box>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Button
                      variant="contained"
                      size="small"
                      color="primary"
                      startIcon={refreshingLive ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon sx={{ fontSize: '18px !important' }} />}
                      onClick={handleCheckUpdate}
                      disabled={refreshingLive}
                      sx={{
                        borderRadius: '12px',
                        fontWeight: 800,
                        textTransform: 'none',
                        px: 2,
                        height: 32,
                        boxShadow: '0 4px 12px rgba(59, 130, 246, 0.25)',
                        '&:hover': { boxShadow: '0 6px 16px rgba(59, 130, 246, 0.35)' }
                      }}
                    >
                      {refreshingLive ? 'Updating...' : 'Check and Update'}
                    </Button>
                    <Box display="flex" alignItems="center" gap={1} sx={{ opacity: 0.8 }}>
                      <ClockIcon sx={{ fontSize: '16px', color: 'text.secondary' }} />
                      <Typography variant="caption" fontWeight={700} color="text.secondary">
                        Last Update: {deviceLiveStatus.lastDataAt ? formatToIST(deviceLiveStatus.lastDataAt, 'hh:mm:ss a') : 'Connecting...'}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>

                {/* Body: the machine drawing on the left, every reading on the
                    right. No column headers and no trend sparkline — the row
                    already reads "name  value unit" without being told so, and
                    the space buys another column of readings instead. Labels
                    are shortened to one line (shortSensorName); the full OEM
                    label is the row's tooltip. */}
                <Box data-testid="live-data-body" sx={{
                  flexGrow: 1, minHeight: 0, display: 'flex',
                  // NEVER wrap on a real screen: the readings belong BESIDE the
                  // drawing, not under it. Only a phone-width card stacks them.
                  flexDirection: { xs: 'column', md: 'row' },
                  flexWrap: 'nowrap',
                  alignItems: 'stretch', gap: 3, px: 3, pb: 3, pt: 1,
                }}>
                  {/* Left: machine position, posed from the newest frame. Fixed
                      width and fixed height — it never grows the card. */}
                  <Box sx={{
                    flex: { xs: '0 0 auto', md: '0 0 440px' },
                    width: { xs: '100%', md: 440 },
                  }}>
                    <MachinePostureCard
                      embedded
                      height={330}
                      frame={livePostureFrame}
                      modelNumber={device?.model_number}
                      category={device?.category || device?.product_category}
                    />
                  </Box>

                  {/* Right: EVERY reading in the frame. This is the ONLY part
                      that scrolls — a machine with a lot of signals lengthens
                      this column, not the card. */}
                  <Box
                    data-testid="live-readings"
                    sx={{ flex: 1, minWidth: 0, minHeight: 0, overflowY: 'auto', pr: 1 }}
                  >
                    {filteredSensors.length === 0 ? (
                      <Box py={4} textAlign="center">
                        <Typography variant="body2" color="text.secondary" fontWeight={700}>
                          {searchTerm ? 'No sensor matches that search' : 'No readings in the latest frame'}
                        </Typography>
                      </Box>
                    ) : (
                      <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                        columnGap: 3,
                      }}>
                        {filteredSensors.map((sensor) => (
                          <Tooltip key={sensor.id} title={sensor.name} placement="top" arrow>
                            <Box sx={{
                              display: 'flex', alignItems: 'center', gap: 1,
                              py: 0.75, borderBottom: 1, borderColor: 'divider',
                              '&:hover': { bgcolor: 'action.hover' },
                            }}>
                              <Typography
                                variant="body2"
                                noWrap
                                sx={{ fontWeight: 700, fontSize: '13px', flexGrow: 1, minWidth: 0 }}
                              >
                                {shortSensorName(sensor.id, sensor.name)}
                              </Typography>
                              <Typography
                                variant="body2"
                                noWrap
                                sx={{ fontWeight: 800, fontFamily: 'monospace', fontSize: '12px', flexShrink: 0 }}
                              >
                                {sensor.value}
                              </Typography>
                              <Typography
                                variant="caption"
                                noWrap
                                sx={{ color: 'text.secondary', fontWeight: 600, fontSize: '11px', flexShrink: 0, width: 34, textAlign: 'left' }}
                              >
                                {sensor.unit && sensor.unit !== '--' ? sensor.unit : ''}
                              </Typography>
                            </Box>
                          </Tooltip>
                        ))}
                      </Box>
                    )}
                  </Box>
                </Box>
              </Card>
            </Grid>

            <Grid item xs={12} md={3} lg={3}>
              {/* Operator — who is running this machine. It sits BESIDE Live
                Data rather than under the map: the readings rarely fill the
                frame's width, so the operator fills space that was empty and
                the page stops pushing him below the fold. Narrow column, so
                the count goes under the title instead of fighting it for the
                same row. Name + emp id always; the mobile is a tel: link so it
                can be dialled from the phone the page is open on. */}
              <Card sx={{ height: 560, display: 'flex', flexDirection: 'column' }}>
                <Box p={2} sx={{ borderBottom: 1, borderColor: 'divider' }}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <OperatorIcon color="primary" />
                    <Typography variant="subtitle2" fontWeight={800}>Operator</Typography>
                  </Box>
                  {!loadingOperators && activeOperators.length > 0 && (
                    <Chip
                      label={`${activeOperators.length} assigned`}
                      size="small"
                      color="success"
                      variant="outlined"
                      sx={{ fontWeight: 700, height: 22, fontSize: '11px', mt: 0.75 }}
                    />
                  )}
                </Box>

                <Box p={2} sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                  {loadingOperators ? (
                    <Stack spacing={1}>
                      <Skeleton variant="rectangular" height={44} sx={{ borderRadius: 2 }} />
                      <Skeleton variant="text" width="60%" />
                    </Stack>
                  ) : activeOperators.length === 0 ? (
                    <Box textAlign="center" py={2}>
                      <OperatorIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 0.5 }} />
                      <Typography variant="body2" fontWeight={700} color="text.secondary">
                        No operator assigned
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {pastOperatorCount > 0
                          ? `${pastOperatorCount} past assignment${pastOperatorCount > 1 ? 's' : ''} on record. Add the current operator from the machine's contract.`
                          : "Operators are added to a machine from its rental contract."}
                      </Typography>
                    </Box>
                  ) : (
                    <Stack spacing={1.5} divider={<Divider flexItem />}>
                      {activeOperators.map((op) => {
                        const name = op.operator_display_name || op.operator_name || 'Unnamed operator';
                        const initials = name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join('');
                        return (
                          <Box key={op.id}>
                            <Box display="flex" alignItems="center" gap={1.25}>
                              <Avatar sx={{ width: 34, height: 34, bgcolor: 'primary.50', color: 'primary.dark', fontSize: 13, fontWeight: 800 }}>
                                {initials || '?'}
                              </Avatar>
                              <Box sx={{ minWidth: 0 }}>
                                <Typography variant="body2" fontWeight={800} noWrap title={name}>{name}</Typography>
                                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                  {op.designation || 'Operator'}
                                </Typography>
                              </Box>
                            </Box>

                            <Stack spacing={0.5} mt={1}>
                              {/* Emp id — the handle Operations and HR both use. */}
                              <Box display="flex" alignItems="center" gap={0.75}>
                                <EmpIdIcon sx={{ fontSize: 15, color: 'text.disabled' }} />
                                {op.emp_id ? (
                                  <Typography variant="caption" fontWeight={700}>{op.emp_id}</Typography>
                                ) : (
                                  <Typography variant="caption" color="text.disabled" fontWeight={600}>No emp id on file</Typography>
                                )}
                              </Box>

                              {/* Mobile — only the roster has it, so say plainly when it is missing. */}
                              <Box display="flex" alignItems="center" gap={0.75}>
                                <PhoneIcon sx={{ fontSize: 15, color: 'text.disabled' }} />
                                {op.mobile ? (
                                  <MuiLink
                                    href={`tel:${op.mobile}`}
                                    underline="hover"
                                    variant="caption"
                                    sx={{ fontWeight: 700 }}
                                  >
                                    {op.mobile}
                                  </MuiLink>
                                ) : (
                                  <Typography variant="caption" color="text.disabled" fontWeight={600}>No mobile on file</Typography>
                                )}
                              </Box>
                            </Stack>

                            {(op.shift || op.from_date) && (
                              <Stack direction="row" spacing={0.75} mt={1} flexWrap="wrap" useFlexGap>
                                {op.shift && (
                                  <Chip label={op.shift} size="small" variant="outlined"
                                    sx={{ height: 20, fontSize: '10px', fontWeight: 700 }} />
                                )}
                                {op.from_date && (
                                  <Chip
                                    icon={<CalendarToday sx={{ fontSize: '11px !important' }} />}
                                    label={`Since ${formatToIST(op.from_date, 'dd MMM yyyy')}`}
                                    size="small"
                                    variant="outlined"
                                    sx={{ height: 20, fontSize: '10px', fontWeight: 700 }}
                                  />
                                )}
                              </Stack>
                            )}
                          </Box>
                        );
                      })}
                    </Stack>
                  )}
                </Box>
              </Card>
            </Grid>

            </Grid>

            {/* Row 2: Metrics · Recent Activity · Operating Thresholds.
                Three across on large screens (4+5+3); on medium the first two
                pair up and the thresholds take the full width below them. */}
            <Grid container spacing={3}>

              {/* Metrics (utilization, last 10 days) */}
              <Grid item xs={12} md={6} lg={4}>
                <Card sx={{ height: 350 }}>
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" mb={4}>
                      <Box>
                        <Typography variant="h6" fontWeight={800}>Metrics</Typography>
                        <Stack direction="row" spacing={2} mt={1}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'primary.main' }} />
                            <Typography variant="caption" fontWeight={700}>
                              {deviceDetails?.utilization?.[deviceDetails.utilization.length - 1]?.working_hours || 0}h (Today)
                            </Typography>
                          </Box>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'error.main' }} />
                            <Typography variant="caption" fontWeight={700} color="error.main">
                              {deviceDetails?.utilization?.reduce((acc, u) => acc + (u.excess_hours || 0), 0).toFixed(1)}h (Extra)
                            </Typography>
                          </Box>
                        </Stack>

                        {/* Status Legend */}
                        <Stack direction="row" spacing={2} mt={1.5}>
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: '#f59e0b' }} />
                            <Typography sx={{ fontSize: '10px', color: 'text.secondary', fontWeight: 600 }}>Maintenance</Typography>
                          </Box>
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: '#94a3b8' }} />
                            <Typography sx={{ fontSize: '10px', color: 'text.secondary', fontWeight: 600 }}>Stock/Ready</Typography>
                          </Box>
                        </Stack>
                      </Box>
                      <Typography variant="caption" color="text.secondary" fontWeight={700}>Last 10 Days</Typography>
                    </Box>
                    <Box height={200}>
                      <Bar
                        data={utilizationData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: {
                            legend: { display: false },
                            tooltip: {
                              filter: (tooltipItem) => tooltipItem.dataset.label !== 'Day Frames'
                            }
                          },
                          scales: {
                            x: { grid: { display: false } }, // Overlapping handled by grouped: false
                            y: {
                              beginAtZero: true,
                              min: 0,
                              max: 24,
                              ticks: {
                                stepSize: 4,
                                callback: (value) => `${value}:00`
                              }
                            }
                          }
                        }}
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Recent Activity — the machine's own ENGINE / IGNITION / GPS /
                  OFF events. It moved here out of row 1 (where it was a narrow
                  3-column sidebar) into the slot Service History used to hold:
                  wider rows, and it frees those 3 columns for Live Data. The
                  service record lives on the Service pages, which this page
                  already links to. */}
              <Grid item xs={12} md={6} lg={5}>
                <Card sx={{ height: 350, display: 'flex', flexDirection: 'column' }}>
                  <CardHeader title="Recent Activity" titleTypographyProps={{ variant: 'subtitle1', fontWeight: 800 }} />
                  <CardContent sx={{ p: '0 !important', flexGrow: 1, overflowY: 'auto' }}>
                  {deviceLiveStatus.recentActivity && deviceLiveStatus.recentActivity.length > 0 ? (
                    deviceLiveStatus.recentActivity.map((activity, i) => {
                      // Backend events: ENGINE / IGNITION / GPS / OFF (with a `color` field).
                      // Engine ON = green, Ignition ON = blue, GPS ON = yellow, Vehicle OFF = red.
                      const palette = activity.color === 'success' ? 'success'   // ENGINE  (green)
                                    : activity.color === 'info'    ? 'info'      // IGNITION (blue)
                                    : activity.color === 'warning' ? 'warning'   // GPS     (yellow)
                                    :                                'error';    // OFF     (red)
                      const Icon = activity.event === 'ENGINE'   ? PlayIcon
                                 : activity.event === 'IGNITION' ? KeyIcon
                                 : activity.event === 'GPS'      ? GpsIcon
                                 :                                  StopIcon;
                      return (
                      <Box key={i} p={2} display="flex" gap={2} sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <Avatar sx={{
                          width: 32, height: 32,
                          bgcolor: `${palette}.light`,
                          color: `${palette}.dark`,
                        }}>
                          <Icon fontSize="small" />
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight={700}>{activity.label}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatToIST(activity.timestamp, 'dd MMM yyyy, hh:mm:ss a')}
                          </Typography>
                        </Box>
                      </Box>
                      );
                    })
                  ) : (
                    <Box p={3} textAlign="center">
                      <Typography variant="body2" color="text.secondary">No activity recorded today</Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
              </Grid>

              {/* Operating Thresholds — the safe band the alarm engine holds this
                  machine to, and where its latest reading sits inside it. This
                  replaced the Alert Monitor tile: an alarm count is already on the
                  Errors & alerts panel and the Alarms page, whereas the bands are
                  what an operations reader needs to judge a live number ("2940 rpm
                  — is that fine?"). Bands, readings and verdicts all come from
                  services/machineHealthChecker via the API, so this can never
                  disagree with the alarms it would raise. */}
              <Grid item xs={12} md={12} lg={3}>
                <Card sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 160 }}>
                  <Box sx={{ p: 2.5, borderBottom: 1, borderColor: 'divider' }}>
                    <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                      <TuneIcon color="primary" />
                      <Typography variant="h6" fontWeight={800}>Operating Thresholds</Typography>
                      {thresholdsOutOfBand > 0 && (
                        <Chip
                          size="small"
                          color="error"
                          label={`${thresholdsOutOfBand} out of band`}
                          sx={{ fontWeight: 700, height: 22, fontSize: '11px' }}
                        />
                      )}
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      {thresholdMeta?.model
                        ? `Safe ranges for ${thresholdMeta.model}`
                        : thresholdMeta?.model_number
                          ? `Safe ranges — model ${thresholdMeta.model_number} not in the spec book, showing the common bands`
                          : 'Safe operating ranges vs the latest frame'}
                      {thresholdMeta?.recorded_at
                        ? ` · read ${formatToIST(thresholdMeta.recorded_at, 'dd MMM, hh:mm a')}`
                        : ''}
                    </Typography>
                  </Box>

                  {thresholdsLoading && thresholds.length === 0 ? (
                    <Box p={4} textAlign="center"><CircularProgress size={22} /></Box>
                  ) : thresholdsError ? (
                    <Box p={4} textAlign="center">
                      <Typography variant="body2" color="text.secondary" fontWeight={700}>
                        Could not load thresholds
                      </Typography>
                      <Typography variant="caption" color="text.disabled">
                        The spec service did not respond. Values on the left are unaffected.
                      </Typography>
                    </Box>
                  ) : thresholdRows.length === 0 ? (
                    <Box p={4} textAlign="center">
                      <TuneIcon sx={{ fontSize: 44, color: 'text.disabled', mb: 1 }} />
                      <Typography variant="body2" color="text.secondary" fontWeight={700}>
                        No thresholds for this machine
                      </Typography>
                      <Typography variant="caption" color="text.disabled">
                        Operating bands are published per model in the OEM spec book.
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ maxHeight: 360, overflowY: 'auto' }}>
                      {thresholdRows.map((t) => {
                        const tone = THRESHOLD_TONE[t.status] || THRESHOLD_TONE['no-data'];
                        return (
                          <Tooltip
                            key={t.id}
                            arrow
                            placement="left"
                            title={t.message || t.source || ''}
                          >
                            <Box sx={{
                              px: 2, py: 1.25,
                              borderBottom: 1, borderColor: 'divider',
                              display: 'flex', alignItems: 'flex-start', gap: 1,
                            }}>
                              <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                                <Typography variant="body2" fontWeight={700} noWrap title={t.label}>
                                  {t.label}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {formatThresholdBand(t)}
                                </Typography>
                              </Box>
                              <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                                <Typography variant="body2" fontWeight={800} sx={{ color: tone.token }}>
                                  {t.value == null ? '--' : `${t.value}${t.unit ? ` ${t.unit}` : ''}`}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  fontWeight={700}
                                  sx={{ color: tone.token, opacity: 0.9 }}
                                >
                                  {tone.label}
                                </Typography>
                              </Box>
                            </Box>
                          </Tooltip>
                        );
                      })}
                    </Box>
                  )}
                </Card>
              </Grid>

            </Grid>

          </Stack>
          : view === 'viz' ? (
            <Stack spacing={3} className="animate-fade-in">
              {/* Silent day: the panels below would each show their own "no data"
                  placeholder, which reads like a loading glitch. Say plainly that
                  the machine sent nothing — that IS the answer to "what happened
                  on that day" for a no-signal row in the daily report. */}
              {vizApplied && !loadingViz && vizData.length === 0 && (
                <Alert severity="warning" sx={{ borderRadius: '16px' }}>
                  <strong>No frames received{vizFromDate === vizToDate && vizFromDate ? ` on ${vizFromDate}` : ' in this range'}.</strong>
                  {' '}The device sent no telemetry at all — no GPS, no ignition, no CAN. It was offline or unpowered for the whole period.
                </Alert>
              )}

              {/* Arrived from a CAN error in the daily report: name the error, so
                  the selected frame below is read as "the machine's state when
                  THIS fired" rather than an arbitrary moment. */}
              {deepAlarm && deepAt && (
                <Alert
                  severity={toneOf(vizAlarms.find(a => a.id === selectedAlarmId)?.severity).color === '#dc2626' ? 'error' : 'info'}
                  icon={<AlertCircleIcon fontSize="inherit" />}
                  sx={{ borderRadius: '16px' }}
                >
                  <strong>{deepAlarm}</strong> reported at{' '}
                  <strong>{formatToIST(deepAt, 'dd MMM yyyy, hh:mm:ss a')}</strong>.
                  {' '}
                  {selectedVizTime?.timestamp
                    ? `Showing the frame the machine sent at ${formatToIST(selectedVizTime.timestamp, 'hh:mm:ss a')} — the map, activity log and every signal below are that moment.`
                    : loadingViz
                      ? 'Loading that day\'s frames…'
                      : 'No telemetry frame was stored near that time, so there is nothing to show for the instant itself.'}
                </Alert>
              )}

              {/* ERRORS & ALERTS — everything the machine (or the health checkers)
                  reported in the period on screen. Each card jumps the whole view
                  to the frame closest to it. */}
              {vizApplied && (
                <Card sx={{ borderRadius: '24px' }}>
                  <Box p={2.5} sx={{ borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                    <AlertCircleIcon sx={{ color: vizAlarms.length ? '#d97706' : 'text.disabled' }} />
                    <Typography variant="h6" fontWeight={800}>Errors &amp; alerts</Typography>
                    <Chip
                      size="small"
                      label={vizAlarms.length}
                      sx={{ fontWeight: 800, height: 22, bgcolor: vizAlarms.length ? '#d97706' : 'grey.300', color: '#fff' }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                      {vizAlarms.length > 0
                        ? 'Click one to see what the machine was doing when it fired'
                        : 'For the selected period'}
                    </Typography>
                    {selectedVizTime && (
                      <Button
                        size="small"
                        onClick={() => { setSelectedVizTime(null); setSelectedAlarmId(null); }}
                        sx={{ textTransform: 'none', fontWeight: 700 }}
                      >
                        Back to all logs
                      </Button>
                    )}
                  </Box>
                  <Box sx={{ p: 2.5 }}>
                    {loadingVizAlarms ? (
                      <Box display="flex" alignItems="center" gap={1.5} py={1}>
                        <CircularProgress size={16} />
                        <Typography variant="body2" color="text.secondary">Loading errors &amp; alerts…</Typography>
                      </Box>
                    ) : vizAlarms.length === 0 ? (
                      <Box display="flex" alignItems="center" gap={1.5} py={1}>
                        <CheckCircleIcon sx={{ color: '#16a34a' }} />
                        <Typography variant="body2" color="text.secondary" fontWeight={600}>
                          No error, warning or alert was reported in this period.
                        </Typography>
                      </Box>
                    ) : (
                      // A single scrolling row: the day reads left→right in time,
                      // and a busy day never pushes the charts off the screen.
                      <Box sx={{
                        display: 'flex', gap: 1.5, overflowX: 'auto', pb: 1,
                        '&::-webkit-scrollbar': { height: 6 },
                        '&::-webkit-scrollbar-thumb': { bgcolor: '#cbd5e1', borderRadius: 3 },
                      }}>
                        {vizAlarms.map((a) => {
                          const tone = toneOf(a.severity);
                          const active = a.id === selectedAlarmId;
                          return (
                            <Card
                              key={a.id}
                              variant="outlined"
                              onClick={() => handleAlarmClick(a)}
                              sx={{
                                flex: '0 0 auto', minWidth: 230, maxWidth: 300, p: 1.5, cursor: 'pointer',
                                borderRadius: '14px', borderLeft: `4px solid ${tone.color}`,
                                transition: 'all .15s',
                                ...(active
                                  ? { borderColor: tone.color, boxShadow: `0 0 0 3px ${tone.color}33`, bgcolor: `${tone.color}0a` }
                                  : { '&:hover': { borderColor: tone.color, transform: 'translateY(-1px)' } }),
                              }}
                            >
                              <Box display="flex" alignItems="center" gap={0.75} mb={0.5} flexWrap="wrap">
                                <Chip
                                  size="small"
                                  label={formatToIST(a.created_at, 'hh:mm:ss a')}
                                  sx={{ height: 20, fontSize: '10px', fontWeight: 800, fontFamily: 'monospace', bgcolor: 'grey.100' }}
                                />
                                <Chip
                                  size="small"
                                  label={tone.label}
                                  sx={{ height: 20, fontSize: '10px', fontWeight: 800, bgcolor: tone.color, color: '#fff' }}
                                />
                                {isDeviceReportedAlarm(a) && (
                                  <Chip
                                    size="small"
                                    label="CAN"
                                    sx={{ height: 20, fontSize: '10px', fontWeight: 800, bgcolor: 'rgba(124,58,237,0.12)', color: '#7c3aed' }}
                                  />
                                )}
                              </Box>
                              <Typography variant="body2" fontWeight={800} noWrap title={a.alarm_name}>
                                {a.alarm_name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" noWrap title={a.message || ''}>
                                {a.message || (a.alarm_status === 'OPEN' ? 'Still open' : 'Closed')}
                              </Typography>
                            </Card>
                          );
                        })}
                      </Box>
                    )}
                  </Box>
                </Card>
              )}

              {/* Machine position — the selected frame's boom/jib/cage angles
                  posed on an articulated side-view rig (base, main boom, jib,
                  cage). Falls back to the newest loaded frame until one is
                  picked, so the card is alive as soon as data loads. */}
              {vizApplied && vizData.length > 0 && (
                <MachinePostureCard
                  frame={selectedVizTime || vizData[vizData.length - 1]}
                  modelNumber={device?.model_number}
                  category={device?.category || device?.product_category}
                />
              )}

              <Grid container spacing={3}>
                {/* Column 1: Map */}
                <Grid item xs={12} md={3}>
                  <Card sx={{ height: 500, width: '100%', display: 'flex', flexDirection: 'column', borderRadius: '24px', overflow: 'hidden' }}>
                    <Box p={2} display="flex" justifyContent="space-between" alignItems="center" sx={{ borderBottom: 1, borderColor: 'divider' }}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <MapIcon color="primary" />
                        <Typography variant="subtitle2" fontWeight={800}>Movement Trail</Typography>
                      </Box>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Stack direction="row" spacing={1} bgcolor="grey.100" p={0.5} borderRadius="10px">
                          <Button size="small" onClick={() => setMapType('satellite')} variant={mapType === 'satellite' ? 'contained' : 'text'} sx={{ fontSize: '10px', minWidth: 'auto', px: 1, height: 24, borderRadius: '8px' }}>SAT</Button>
                          <Button size="small" onClick={() => setMapType('map')} variant={mapType === 'map' ? 'contained' : 'text'} sx={{ fontSize: '10px', minWidth: 'auto', px: 1, height: 24, borderRadius: '8px' }}>MAP</Button>
                        </Stack>
                      </Stack>
                    </Box>
                    <Box sx={{ flexGrow: 1, position: 'relative' }}>
                      {(selectedVizTime && selectedVizTime.timestamp) ? (
                        (selectedVizTime.latitude || selectedVizTime.lat) ? (
                          <MapContainer center={[parseFloat(selectedVizTime.latitude || selectedVizTime.lat), parseFloat(selectedVizTime.longitude || selectedVizTime.lon)]} zoom={15} style={{ height: '100%', width: '100%' }}>
                            <TileLayer url={mapType === 'satellite' ? "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}" : "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"} />
                            <Marker position={[parseFloat(selectedVizTime.latitude || selectedVizTime.lat), parseFloat(selectedVizTime.longitude || selectedVizTime.lon)]}>
                              <Popup open autoplay>
                                <Typography variant="subtitle2" fontWeight={800}>Selected Time</Typography>
                                <Typography variant="caption" display="block">{formatToIST(selectedVizTime.timestamp, 'hh:mm:ss a')}</Typography>
                                <Typography variant="caption" display="block" color="text.secondary">
                                  Lat: {parseFloat(selectedVizTime.latitude || selectedVizTime.lat).toFixed(6)}<br />
                                  Lon: {parseFloat(selectedVizTime.longitude || selectedVizTime.lon).toFixed(6)}
                                </Typography>
                              </Popup>
                            </Marker>
                          </MapContainer>
                        ) : (
                          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="100%" bgcolor="grey.50" gap={2}>
                            <MapIcon sx={{ fontSize: 64, color: 'grey.300' }} />
                            <Typography variant="h6" fontWeight={700} color="text.secondary">Location Data Not Available</Typography>
                          </Box>
                        )
                      ) : gpsTrail.length > 0 ? (
                        <MapContainer center={gpsTrail[0]} zoom={15} style={{ height: '100%', width: '100%' }}>
                          <TileLayer url={mapType === 'satellite' ? "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}" : "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"} />
                          <Polyline positions={gpsTrail} color="#3b82f6" weight={4} opacity={0.8} />
                          <Marker position={gpsTrail[0]}><Popup>Start Point</Popup></Marker>
                          <Marker position={gpsTrail[gpsTrail.length - 1]}><Popup>End Point</Popup></Marker>
                        </MapContainer>
                      ) : (
                        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="100%" bgcolor="grey.50" gap={2}>
                          <MapIcon sx={{ fontSize: 64, color: 'grey.300' }} />
                          <Typography variant="h6" fontWeight={700} color="text.secondary">No GPS Trail</Typography>
                        </Box>
                      )}
                    </Box>
                  </Card>
                </Grid>

                {/* Column 2: Historical Activity Log */}
                <Grid item xs={12} md={3}>
                  <Card sx={{ height: 500, display: 'flex', flexDirection: 'column', borderRadius: '24px' }}>
                    <Box p={3} sx={{ borderBottom: 1, borderColor: 'divider' }}>
                      <Typography variant="h6" fontWeight={800}>Activity Log</Typography>
                      <Typography variant="caption" color="text.secondary">State changes for selected period</Typography>
                    </Box>
                    <TableContainer sx={{ flexGrow: 1, overflowY: 'auto' }}>
                      <Table stickyHeader size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 700, fontSize: '10px' }}>STATE EVENT</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 700, fontSize: '10px' }}>TIME</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(() => {
                            let activitiesToRender = vizActivities;
                            if (selectedVizTime && selectedVizTime.timestamp) {
                              const activeState = vizActivities.find(act => new Date(act.timestamp) <= new Date(selectedVizTime.timestamp));
                              activitiesToRender = activeState ? [activeState] : [];
                            }
                            return activitiesToRender.length > 0 ? (
                              activitiesToRender.map((act, i) => (
                                <TableRow key={i} hover sx={{ bgcolor: (selectedVizTime && selectedVizTime.timestamp) ? 'rgba(59, 130, 246, 0.1)' : 'inherit' }}>
                                  <TableCell sx={{ py: 1.5 }}>
                                    <Box display="flex" alignItems="center" gap={1}>
                                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: `${act.color}.main` }} />
                                      <Typography variant="body2" sx={{ fontWeight: (selectedVizTime && selectedVizTime.timestamp) ? 900 : 800, fontSize: '0.85rem' }}>{act.label}</Typography>
                                    </Box>
                                  </TableCell>
                                  <TableCell align="center">
                                    <Typography variant="caption" sx={{ fontWeight: (selectedVizTime && selectedVizTime.timestamp) ? 800 : 700, color: (selectedVizTime && selectedVizTime.timestamp) ? 'primary.main' : 'text.secondary' }}>{formatToIST(act.timestamp, 'dd MMM hh:mm:ss a')}</Typography>
                                  </TableCell>
                                </TableRow>
                              ))
                            ) : (
                              <TableRow><TableCell colSpan={2} align="center" sx={{ py: 10 }}><Typography variant="caption" color="text.disabled">No activities found</Typography></TableCell></TableRow>
                            );
                          })()}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Card>
                </Grid>

                {/* Column 3: Data Log (Detailed Data Snapshot) */}
                <Grid item xs={12} md={3}>
                  <Card sx={{ height: 500, display: 'flex', flexDirection: 'column', borderRadius: '24px' }}>
                    <Box p={3} sx={{ borderBottom: 1, borderColor: 'divider' }}>
                      <Typography variant="h6" fontWeight={800}>Data Log</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {selectedVizTime?.timestamp ? formatToIST(selectedVizTime.timestamp, 'dd MMM yyyy, hh:mm:ss a') : vizApplied ? 'Select a time' : 'Latest Data'}
                      </Typography>
                    </Box>
                    <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 3, bgcolor: 'grey.50' }}>
                      {loadingViz ? (
                        <Box display="flex" alignItems="center" justifyContent="center" height="100%"><Typography>Analyzing Data...</Typography></Box>
                      ) : (selectedVizTime && selectedVizTime.timestamp) ? (
                        <Stack spacing={2}>
                          {(() => {
                            let displayData = { ...selectedVizTime };
                            
                            // 1. Merge Payload
                            if (selectedVizTime.payload) {
                              try {
                                const extra = typeof selectedVizTime.payload === 'string' 
                                  ? JSON.parse(selectedVizTime.payload) 
                                  : selectedVizTime.payload;
                                displayData = { ...displayData, ...extra };
                              } catch (e) {}
                            }

                            // 2. Merge Location
                            if (selectedVizTime.location) {
                              try {
                                const loc = typeof selectedVizTime.location === 'string' 
                                  ? JSON.parse(selectedVizTime.location) 
                                  : selectedVizTime.location;
                                displayData = { ...displayData, ...loc };
                              } catch (e) {}
                            }
                            
                            return Object.keys(displayData)
                              .filter(k => !['id', 'device_id', 'timestamp', 'latitude', 'longitude', 'lat', 'lon', 'location', 'fields', 'time', 'fullTime', 'device_metadata', 'device_data'].includes(k))
                              .map((k, i) => (
                                <Card key={i} variant="outlined" sx={{ p: 1.5, borderRadius: '12px', bgcolor: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ fontSize: '0.75rem' }}>{formatParameterName(k)}</Typography>
                                  <Typography variant="body2" fontWeight={800} color="text.primary">{formatParameterValue(k, displayData[k])}</Typography>
                                </Card>
                              ));
                          })()}
                        </Stack>
                      ) : (
                        <Stack spacing={2}>
                          {vizApplied && vizData.length > 0 ? (
                            /* Show mini trend charts after Apply */
                            Object.keys(allTrendData).map((k, i) => (
                              <Card key={i} variant="outlined" sx={{ p: 1.5, borderRadius: '12px', bgcolor: 'white', display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Box sx={{ minWidth: 150 }}>
                                  <Typography variant="caption" color="text.secondary" display="block" fontWeight={700} sx={{ fontSize: '0.75rem' }}>
                                    {formatParameterName(k)}
                                  </Typography>
                                </Box>
                                <Box sx={{ flexGrow: 1, height: 60 }}>
                                  <Line data={allTrendData[k]} options={miniChartOptions} />
                                </Box>
                              </Card>
                            ))
                          ) : (
                            /* Default: Show latest telemetry snapshot */
                            Object.entries(latestTelemetry)
                              .filter(([k]) => !['id', 'device_id', 'timestamp', 'latitude', 'longitude', 'lat', 'lon', 'location', 'fields', 'time', 'fullTime', 'device_metadata', 'device_data'].includes(k))
                              .map(([k, v], i) => (
                                <Card key={i} variant="outlined" sx={{ p: 1.5, borderRadius: '12px', bgcolor: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ fontSize: '0.75rem' }}>{formatParameterName(k)}</Typography>
                                  <Typography variant="body2" fontWeight={800} color="text.primary">{formatParameterValue(k, v)}</Typography>
                                </Card>
                              ))
                          )}
                          {!vizApplied && Object.keys(latestTelemetry).length === 0 && (
                            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={6} gap={1}>
                              <AnalyticsIcon sx={{ fontSize: 48, color: 'grey.300' }} />
                              <Typography variant="body2" color="text.secondary" fontWeight={600}>Select dates and click Apply</Typography>
                            </Box>
                          )}
                        </Stack>
                      )}
                    </Box>
                  </Card>
                </Grid>

                {/* Column 4: Device Events — the RAW device_alerts + device_dtc
                    rows for the selected day range. Clicking a row jumps the
                    whole view (map, activity, Data Log) to that moment. */}
                <Grid item xs={12} md={3}>
                  <Card sx={{ height: 500, display: 'flex', flexDirection: 'column', borderRadius: '24px' }}>
                    <Box p={3} sx={{ borderBottom: 1, borderColor: 'divider' }}>
                      <Typography variant="h6" fontWeight={800}>Device Events</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Alerts &amp; DTC the device reported{vizFromDate ? ` · ${vizFromDate === vizToDate ? vizFromDate : `${vizFromDate} → ${vizToDate}`}` : ''}
                      </Typography>
                    </Box>
                    <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 2, bgcolor: 'grey.50' }}>
                      <Typography variant="overline" sx={{ fontWeight: 800, color: 'text.secondary', display: 'block', mb: 0.5 }}>
                        Alert data ({vizEvents.alerts.length})
                      </Typography>
                      {vizEvents.alerts.length === 0 ? (
                        <Typography variant="caption" color="text.secondary" fontStyle="italic" sx={{ display: 'block', mb: 1.5 }}>
                          No device alerts on this day.
                        </Typography>
                      ) : (
                        <Stack spacing={1} sx={{ mb: 2 }}>
                          {vizEvents.alerts.map(a => (
                            <Card key={`al-${a.id}`} variant="outlined"
                              onClick={() => a.received_at && selectFrameNearest(a.received_at)}
                              title="Show what the machine was doing at this moment"
                              sx={{ p: 1.25, borderRadius: '12px', bgcolor: 'white', cursor: 'pointer', '&:hover': { borderColor: 'primary.main' } }}>
                              <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
                                <Typography variant="body2" fontWeight={800} noWrap>{a.alert_code || 'Alert'}</Typography>
                                <Chip size="small" label={(a.severity || 'info').toUpperCase()}
                                  color={/crit|error/i.test(a.severity || '') ? 'error' : /warn/i.test(a.severity || '') ? 'warning' : 'default'}
                                  sx={{ height: 18, fontSize: 9, fontWeight: 800 }} />
                              </Box>
                              <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }} title={a.message || ''}>
                                {a.received_at ? formatToIST(a.received_at, 'hh:mm:ss a') : ''}{a.message ? ` · ${a.message}` : ''}
                              </Typography>
                            </Card>
                          ))}
                        </Stack>
                      )}

                      <Typography variant="overline" sx={{ fontWeight: 800, color: 'text.secondary', display: 'block', mb: 0.5 }}>
                        DTC data ({vizEvents.dtcs.length})
                      </Typography>
                      {vizEvents.dtcs.length === 0 ? (
                        <Typography variant="caption" color="text.secondary" fontStyle="italic" sx={{ display: 'block' }}>
                          No fault codes from the vehicle CAN on this day.
                        </Typography>
                      ) : (
                        <Stack spacing={1}>
                          {vizEvents.dtcs.map(d => (
                            <Card key={`dt-${d.id}`} variant="outlined"
                              onClick={() => d.received_at && selectFrameNearest(d.received_at)}
                              title="Show what the machine was doing at this moment"
                              sx={{ p: 1.25, borderRadius: '12px', bgcolor: 'white', cursor: 'pointer', '&:hover': { borderColor: 'primary.main' } }}>
                              <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
                                <Typography variant="body2" fontWeight={800} noWrap>
                                  {d.code || `SPN ${d.spn ?? '-'} · FMI ${d.fmi ?? '-'}`}
                                </Typography>
                                <Chip size="small" label={Number(d.active) === 1 ? 'ACTIVE' : 'CLEARED'}
                                  color={Number(d.active) === 1 ? 'error' : 'default'}
                                  sx={{ height: 18, fontSize: 9, fontWeight: 800 }} />
                              </Box>
                              <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}
                                title={d.description || ''}>
                                {d.received_at ? formatToIST(d.received_at, 'hh:mm:ss a') : ''}
                                {d.description ? ` · ${d.description}` : ''}
                                {d.occurrence_count ? ` · ×${d.occurrence_count}` : ''}
                              </Typography>
                            </Card>
                          ))}
                        </Stack>
                      )}
                    </Box>
                  </Card>
                </Grid>
              </Grid>

              {/* Bottom Row: Dual Trend Graphs */}
              <Grid container spacing={3}>
                <Grid item xs={12} lg={6}>
                  <Card sx={{ p: 3, borderRadius: '24px', height: 450, width: '100%' }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                      <Typography variant="h6" fontWeight={800}>Trend Analysis 1</Typography>
                      <FormControl size="small" sx={{ minWidth: 200, maxWidth: 300 }}>
                        <Select
                          multiple
                          displayEmpty
                          value={leftTrendParams}
                          onChange={(e) => {
                            const val = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
                            if (val.length <= 5) setLeftTrendParams(val);
                          }}
                          renderValue={(selected) => {
                            if (selected.length === 0) return <Typography variant="caption" color="text.secondary">Select Signals</Typography>;
                            return (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {selected.map((value) => (
                                  <Chip key={value} label={formatParameterName(value)} size="small" sx={{ height: 20, fontSize: '10px' }} />
                                ))}
                              </Box>
                            );
                          }}
                          sx={{ borderRadius: '12px' }}
                        >
                          {availableVizParameters.map(k => (
                              <MenuItem key={k} value={k}>{formatParameterName(k)}</MenuItem>
                            ))}
                        </Select>
                      </FormControl>
                    </Box>
                    <Box sx={{ height: 350 }}>
                      {vizApplied && vizData.length > 0 && leftTrendParams.length > 0 ? (
                        <Line data={leftTrendData} options={trendChartOptions} />
                      ) : (
                        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="100%" gap={1}>
                          <TimelineIcon sx={{ fontSize: 48, color: 'grey.300' }} />
                          <Typography color="text.secondary" fontWeight={600}>{!vizApplied ? 'Click Apply to load trend data' : 'Select parameters to view trends'}</Typography>
                        </Box>
                      )}
                    </Box>
                  </Card>
                </Grid>
                <Grid item xs={12} lg={6}>
                  <Card sx={{ p: 3, borderRadius: '24px', height: 450, width: '100%' }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                      <Typography variant="h6" fontWeight={800}>Trend Analysis 2</Typography>
                      <FormControl size="small" sx={{ minWidth: 200, maxWidth: 300 }}>
                        <Select
                          multiple
                          displayEmpty
                          value={rightTrendParams}
                          onChange={(e) => {
                            const val = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
                            if (val.length <= 5) setRightTrendParams(val);
                          }}
                          renderValue={(selected) => {
                            if (selected.length === 0) return <Typography variant="caption" color="text.secondary">Select Signals</Typography>;
                            return (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {selected.map((value) => (
                                  <Chip key={value} label={formatParameterName(value)} size="small" sx={{ height: 20, fontSize: '10px' }} />
                                ))}
                              </Box>
                            );
                          }}
                          sx={{ borderRadius: '12px' }}
                        >
                          {availableVizParameters.map(k => (
                              <MenuItem key={k} value={k}>{formatParameterName(k)}</MenuItem>
                            ))}
                        </Select>
                      </FormControl>
                    </Box>
                    <Box sx={{ height: 350 }}>
                      {vizApplied && vizData.length > 0 && rightTrendParams.length > 0 ? (
                        <Line data={rightTrendData} options={trendChartOptions} />
                      ) : (
                        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="100%" gap={1}>
                          <TimelineIcon sx={{ fontSize: 48, color: 'grey.300' }} />
                          <Typography color="text.secondary" fontWeight={600}>{!vizApplied ? 'Click Apply to load trend data' : 'Select parameters to view trends'}</Typography>
                        </Box>
                      )}
                    </Box>
                  </Card>
                </Grid>
              </Grid>
            </Stack>
          ) : null}
    </Box>
  );
};

export default MEWPDetailPage;